// Rome, "Coins in the Trevi at 11 pm" (a mild night in May). The city's first look: rome-colosseum.js is its other one
// (the registry's `looks`). We stand at the top of the steps on the south side
// of Piazza di Trevi and face north: the fountain fills the palace front across the basin. Back to front: a strip of night
// sky with a thin moon -> Palazzo Poli's south front, nine bays wide, floodlit warm from below: the wings with their giant
// Corinthian pilasters and three bays of windows each, and in the middle the triumphal arch with four columns, the attic
// with its four statues, the inscription and the papal arms held by two Fames; Oceanus, bearded and cloaked, in the great
// niche on his scalloped shell chariot, Abundance (left) and Salubrity (right) in the side niches under their reliefs -> the
// scogliera of carved travertine boulders with the restless sea horse (left) and the calm one (right), each with its triton;
// the water runs off the shell and breaks over the rock lips in ragged sheets and streams, widest under the chariot
// -> the wide turquoise basin, lit from under the water -> the rim, where people toss coins over their shoulders -> the
// lower terrace and the steps, packed: couples, selfies, a rose seller, a man launching light-up toys, another blowing
// soap bubbles, two carabinieri, a gelato queue on the west side (left), the bar and the tobacconist on the east side
// (right), and at the right edge the columns of Santi Vincenzo e Anastasio. Once a loop a tour group lines up and throws
// all at once, a man proposes, and a wedding couple poses for their photographer (the timeline is further down).
// The palace's east wing is under restoration: a tube scaffold carries the lit hoarding of a made-up coffee, CAFFE LUPA
// (its face is the exported `billboard`; render(t, { pitch }) swaps the ad, render(t, { billboard: false }) leaves the
// scaffold and the hoarding out). No falling thing of its own: the fountain's spray is the weather here.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, rect, makeHalo, drawHalo, text, textWidth, FONT3, makePitch, reflect } from '../engine/core.js';
import { weatherPlans, drawWeather, STORM_FLASHES } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
// The front is drawn as an elevation, symmetrical about x = 239.5 (mirror: x -> 479 - x), 240 px for its 49 m.
const CX = 240;                                       // the fountain's axis
const TIP = 24;                                       // top row of the papal arms
const ATTIC = 38, ENT = 59, COLS = 70, PED = 119, BASE = 129, WL = 152;   // rows: attic cornice, entablature, capitals, pedestals, the ledge over the rocks, waterline
const PAL = [120, 359];                               // the palace front, first and last column
const ARCH = [192, 287];                              // the triumphal arch: columns at 192..196, 215..219, 260..264, 283..287
const NICHE = { cx: 239.5, cy: 92, r: 17.6 };         // the great niche: its round head springs at row 92
const RIM = 196;                                      // top of the basin's near rim
const BASIN = { l0: 122, r0: 357, l1: 40, r1: 439 };  // the water's edges at the waterline (0) and at the near rim (1)
const TERR = 202;                                     // the lower terrace starts under the rim
const STEPS = [212, 230, 248, 266];                   // far edge (nosing) of each step up towards us
const VP = { x: 240, y: 128 };                        // the side walls of the piazza run back to this point
const SIDE = { far: 104, ground: 151, roof: 60 };     // west wall: its far end's column, ground row and roof row (east wall: mirrored)
const BB = { x: 290, y: 73, w: 44, h: 20 };           // the hoarding's face on the east wing's scaffold (the pitch is painted here)
const BB_LAMPS = [300, 323];                          // its two floodlamps, on arms above the face

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** posterised pick: flat bands with a narrow dither seam (cut stone, printed posters) */
function pickFlat(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * 0.34) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** a sprite from rows of palette keys ('.' = transparent) */
function sprite(rows, pal, flip = false) {
  const w = Math.max(...rows.map(r => r.length)), p = pix(w, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (pal[row[i]]) p.set(flip ? w - 1 - i : i, j, pal[row[i]]); });
  return p.done();
}
/** FONT3 text into a pix buffer */
function textP(p, s, x, y, col, sc = 1) {
  let cx = x;
  for (const ch of s) { const g = FONT3[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.fill(cx + i * sc, y + j * sc, sc, sc, col); }); cx += 4 * sc; }
}
/** #rrggbb between two #rrggbb colours */
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (!MIXED[k]) { const A = C(a), B = C(b); MIXED[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * t).toString(16).padStart(2, '0')).join(''); }
  return MIXED[k];
}
/** flattened dithered ellipse of light (pools on the ground), drawn additively */
function makePool(rx, ry, col, maxA) {
  const w = rx * 2 + 1, h = ry * 2 + 1, p = pix(w, h), c = hex(col);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
const mir = x => x < CX ? 479 - x : x;                // the front's right half stands for both
const inR = (v, a, b) => v >= a && v <= b;

/* ================= Sky: a mild May night, the fountain's warm haze over the roofs, a thin moon ================= */
const SKY = ['#070a22', '#0a0e2a', '#0e1232', '#12163a', '#171a42', '#1d1e4a', '#252250', '#2f2656', '#3b2b5a', '#48305c', '#57365c', '#683e5c'];
const SKY_H = 160;
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 120), 1.15) * 0.7;
    v += Math.max(0, 1 - Math.hypot((i - CX) / 250, (y - 64) / 70)) * 0.34;      // the floodlit front's glow
    p.set(i, y, y < SKY_H ? pick(SKY, v, i, y) : '#0c0a16');
  }
  // a thin waxing crescent over the east wing, tilted as it sets
  const mx = 326, my = 12;
  for (let y = -5; y <= 5; y++) for (let x = -5; x <= 5; x++) {
    const d = Math.hypot(x, y), d2 = Math.hypot(x + 2.2, y + 1.1);
    if (d <= 4.6 && d2 > 4.5) p.set(mx + x, my + y, d > 3.8 ? '#f4e6c0' : '#fff8e2');
    else if (d <= 4.6 && hash(x + 9, y + 9, 2) < 0.25) p.set(mx + x, my + y, '#1b1c44');    // earthshine
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(77);
  for (let k = 0; k < 400 && STARS.length < 46; k++) {
    const x = Math.floor(r() * W), y = 1 + Math.floor(r() * 34);
    if (Math.hypot(x - 326, y - 12) < 9) continue;
    if (y > 22 && Math.abs(x - CX) < 20) continue;                 // behind the papal arms
    STARS.push({ x, y, k: 20 + Math.floor(r() * 60), ph: r(), b: r() < 0.12 ? 0.9 : 0.25 + r() * 0.4, col: r() < 0.3 ? '#ffe2c0' : r() < 0.6 ? '#cfe0ff' : '#f0eeff' });
  }
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) { ctx.fillStyle = s.col; ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, 326, 12, 0.8);
}

/* ================= Palazzo Poli's front: travertine, floodlit warm from below ================= */
const STONE = ['#241828', '#3a2834', '#563e40', '#77584a', '#987658', '#ba966a', '#d6b480', '#eed09c', '#fce6be', '#fff7de'];
const MARBLE = ['#3a2e40', '#5c4e56', '#86766e', '#ae9e88', '#d2c2a4', '#ecdebe', '#fdf2d8', '#ffffff'];
const NICHE_R = ['#22162a', '#3a2836', '#583e40', '#7a5846', '#9e7650', '#c0965e', '#deb674'];
const WINS = [];                                      // palace windows that switch on and off: { id, x, y, w, h, n, off }
function facadeLight(x, y) {
  const d = Math.abs(x - 239.5) / 120;
  return 0.51 + 0.2 * smooth(44, 150, y) - 0.09 * d * d + 0.1 * Math.max(0, 1 - Math.hypot((x - 239.5) / 60, (y - 118) / 60));
}
/** one pixel of the front: [ramp, level], a colour, or null for the sky behind */
function facadePixel(x, y) {
  const m = mir(x), left = x < CX, L = facadeLight(x, y), arch = m <= ARCH[1];
  if (y < ATTIC) {                                     // above the attic: a balustrade over the arch (the arms are stamped on)
    if (!arch || y < 33 || m < 254) return null;
    if (y === 33) return [STONE, L + 0.16];
    if (y === 37) return [STONE, L + 0.04];
    return (x & 1) ? [STONE, L + 0.06] : null;
  }
  if (y <= 40) return [STONE, L + (y === 40 ? 0.2 : y === 38 ? -0.06 : 0.06) + (arch ? 0.04 : 0)];   // attic cornice
  const pier = arch && (inR(m, 259, 265) || inR(m, 282, 287));    // the arch breaks forward over each column
  if (y < ENT) {                                       // attic storey
    if (pier) return [STONE, L + 0.08];
    if (arch && m < 259) {                             // the inscription of Clement XII
      if (m <= 255 && inR(y, 43, 55)) {
        if (y === 43 || y === 55 || m === 255) return [STONE, L + 0.02];
        if ((y & 1) && inR(y, 45, 53) && m - 240 < [13, 9, 14, 11, 12][(y - 45) >> 1] && hash(x, y, 9) < 0.8) return [STONE, L - 0.22];
        return [STONE, L + 0.14];
      }
      return [STONE, L - 0.02];
    }
    if (arch) {                                        // carved panels over the side bays
      if (inR(m, 268, 279) && inR(y, 45, 54))
        return [STONE, L + (y === 45 || y === 54 || m === 268 || m === 279 ? 0.1 : -0.08 + (vnoise(x / 2, y / 2, 7) - 0.5) * 0.16)];
      return [STONE, L - 0.02];
    }
    const b = (m - 291) % 23;                          // the wings' attic: a small window in each bay
    if (m >= 291 && inR(b, 7, 12) && inR(y, 45, 52))
      return (b === 7 || b === 12 || y === 45 || y === 52) ? [STONE, L + 0.1] : (y < 48 ? '#262238' : '#15111e');
    return [STONE, L - 0.07];
  }
  if (y < COLS) {                                      // entablature: cornice, frieze, architrave (lit from underneath)
    let d = y === 59 ? -0.04 : y <= 61 ? 0.08 : y === 62 ? 0.24 : y <= 66 ? -0.05 : y === 67 ? 0.14 : 0.03;
    if (pier) d += 0.09; else if (arch) d += 0.02;
    if (y === 62 && (x & 1)) d -= 0.12;               // dentils
    return [STONE, L + d];
  }
  if (y >= BASE) {                                     // rusticated basement (the rocks cover most of it)
    if (y === BASE) return [STONE, L + 0.18];
    const row = Math.floor((y - 130) / 5);
    if ((y - 130) % 5 === 4 || (m + (row & 1) * 5) % 10 === 0) return [STONE, L - 0.22];
    return [STONE, L - 0.05 + (hash(m >> 1, row, 11) - 0.5) * 0.08];
  }
  if (!arch) {                                         // the wings: giant pilasters, three bays of windows
    const q = (m - 288) % 23;
    if (q < 3) {
      const i = left ? 2 - q : q;
      if (y <= 75) return [STONE, L + 0.12 + (hash(x, y, 5) < 0.4 ? -0.16 : 0.05)];
      if (y >= 124) return [STONE, L + (y === 126 ? -0.06 : 0.1)];
      return [STONE, L + (i === 0 ? 0.17 : i === 1 ? 0.08 : -0.13)];
    }
    const b = q - 3;
    if (y <= 75 && (b === 0 || b === 19)) return [STONE, L + 0.05];
    if (y >= PED) return [STONE, L - 0.08 + (y === PED ? 0.2 : y === 128 ? 0.1 : 0)];
    if (inR(b, 6, 12) && inR(y, 76, 88)) {             // upper windows
      if (b === 6 || b === 12 || y === 76 || y === 88) return [STONE, L + 0.1];
      return b === 9 || y === 82 ? '#2c2536' : y < 80 ? '#221d32' : '#15111e';
    }
    if (y === 89 && inR(b, 5, 13)) return [STONE, L + 0.18];
    if (inR(b, 6, 12) && inR(y, 96, 113)) {            // piano nobile windows
      if (b === 6 || b === 12 || y === 96 || y === 113) return [STONE, L + 0.1];
      return b === 9 || y === 102 || y === 107 ? '#2c2536' : y < 101 ? '#221d32' : '#15111e';
    }
    const ped = y === 95 ? 5 : y === 94 ? 4 : y === 93 ? 2 : y === 92 ? 0 : -1;   // their pediments
    if (ped >= 0 && Math.abs(b - 9) <= ped) return [STONE, L + (y === 95 ? 0.2 : Math.abs(b - 9) === ped ? 0.14 : -0.04)];
    if (inR(y, 114, 116) && inR(b, 5, 13)) return [STONE, L + (y === 114 ? 0.2 : y === 116 ? 0.08 : (b & 1) ? 0.1 : -0.2)];   // balconies
    return [STONE, L - 0.08];
  }
  for (const c0 of [260, 283]) {                       // the four Corinthian columns on their pedestals
    const i0 = m - c0;
    if (i0 < -1 || i0 > 5) continue;
    const i = left ? 4 - i0 : i0;
    if (y <= 75) return [STONE, L + 0.14 + (hash(x, y, 5) < 0.45 ? -0.18 : 0.06) + (y === 70 ? 0.08 : 0)];
    if (y >= PED) return [STONE, L + (y === PED || y === 128 ? 0.18 : i <= 0 ? 0.1 : i >= 4 ? -0.14 : 0)];
    if (y >= 115) return [STONE, L + (y === 115 || y === 117 ? 0.16 : -0.02)];
    if (i0 >= 0 && i0 <= 4) return [STONE, L + [0.2, 0.13, 0.03, -0.09, -0.2][i]];
  }
  if (m >= 265) {                                      // side bays: a relief over a statue's niche
    const b = m - 265;
    if (inR(b, 3, 14) && inR(y, 76, 90)) {
      if (b === 3 || b === 14 || y === 76 || y === 90) return [STONE, L + 0.1];
      return [STONE, L - 0.1 + (vnoise(x / 1.6, y / 4, left ? 21 : 22) - 0.5) * 0.34];
    }
    if (inR(b, 4, 13) && inR(y, 95, 121) && !(y < 100 && Math.hypot(b - 8.5, y - 100) > 5.2)) {
      if (b === 4 || b === 13 || (y < 100 && Math.hypot(b - 8.5, y - 100) > 4.2)) return [STONE, L + 0.1];
      return [NICHE_R, 0.22 + 0.55 * smooth(98, 121, y) - Math.abs(b - 8.5) * 0.04];
    }
    if (y === 93 || y === 123) return [STONE, L + 0.1];
    return [STONE, L - 0.05];
  }
  // the middle bay: the great niche with its coffered half-dome, on four small Ionic columns
  const dx = x - NICHE.cx, r = Math.hypot(dx, y - NICHE.cy);
  if (m >= 258) return [STONE, L + 0.06];
  if (!(y < NICHE.cy ? r < NICHE.r : Math.abs(dx) < NICHE.r)) return [STONE, L + (y < NICHE.cy && r < NICHE.r + 2 ? 0.16 : -0.02)];
  if (y < NICHE.cy) {
    const rr = r / NICHE.r, th = Math.atan2(NICHE.cy - y, dx) / Math.PI * 9;
    const rib = Math.abs(rr - 0.48) < 0.07 || Math.abs(rr - 0.78) < 0.06 || Math.abs(th - Math.floor(th) - 0.5) > 0.34;
    return [NICHE_R, 0.3 + 0.3 * smooth(74, 92, y) + (rib ? 0.18 : -0.1)];
  }
  if (y <= 94) return [STONE, L + (y === 92 ? 0.18 : y === 94 ? -0.12 : 0.04)];
  if (inR(m, 252, 253) || inR(m, 256, 257)) {
    if (y === 95 || y >= 122) return [STONE, L + 0.16];
    return [STONE, L + ((left ? (m & 1) === 1 : (m & 1) === 0) ? 0.16 : -0.1)];
  }
  return [NICHE_R, 0.26 + 0.5 * smooth(96, 126, y) - 0.3 * (dx / NICHE.r) ** 2];
}

/* ----- the sculpture: rows of shade keys, stamped in marble and lit from below ----- */
const SHADE = { a: 0.3, l: 0.15, m: 0, d: -0.18, s: -0.34 };
function stampStone(p, rows, x0, y0, L0, flip = false, shadow = null) {
  const w = Math.max(...rows.map(r => r.length)), ramp = MARBLE;
  if (shadow) rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (SHADE[row[i]] !== undefined) {   // its shadow, thrown up the wall behind
    const x = x0 + (flip ? w - 1 - i : i), y = y0 + j;
    p.set(x + 1, y - 1, shadow); p.set(x, y - 1, shadow); p.set(x + 1, y, shadow);
  } });
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) {
    const s = SHADE[row[i]];
    if (s === undefined) continue;
    const x = x0 + (flip ? w - 1 - i : i), y = y0 + j;
    p.set(x, y, pick(ramp, L0 + s + 0.16 * j / rows.length, x, y));
  } });
}
// Oceanus stands in his shell chariot: curly hair and a full beard, his right arm flung out over the water, drapery round
// his hips, and the mantle hanging from his left shoulder and flaring out behind him to a ragged hem (body on column 9)
const OCEANUS = [
  '........mmm...........', '.......mlmlm..........', '.......mlalm..........', '.......lsasl..........', '.......dlmld..........',
  '......smldlmsdm.......', '.....mlsdmdslsmd......', '...mllalllllsdmmd.....', '.mlm..lallllsdsmdm....', 'mlm...llalllsdsmdmd...',
  'lm....mllllldmsmdsmd..', 'l.....dmmmmmsmsmdsmd..', '......mlllmmsdsmdsmdd.', '......llmllmsdsmdsmsd.', '......llm.llmdsmdsmsdd',
  '.....llm...llmsmdsmsdd', '.....llm...lllmsdsdmd.', '.....lld....llm.d.dd.d', '.....llm....llm.......', '.....llm.....llm......',
  '.....lmd.....llm......', '.....lmd.....llm......', '....lllm....mlllm.....',
];
const ATTIC_STATUES = [
  ['.l.', 'mld', '.m.', 'llm.', 'lmdl', 'lld.', 'lmd', 'lmd', 'llm', 'lmd', 'lmd', 'llm', 'mmd'],
  ['.l.', 'mld', '.m.', 'llm', 'lmd', 'allm', 'lmd', 'lmd', 'lld', 'lmd', 'lmd', 'llm', 'mmd'],
];
const ABUNDANCE = ['..ml..', '..lld.', '..md..', '.lllm.', 'alllmd', 'lallm.', '.lllm.', '.llmd.', '.lmld.', '.llmd.', '.lmld.', '.llmd.', '.lmld.', '.llmd.', '.lmlm.', 'mllmd.', 'allmd.', 'mmmmd.'];
const SALUBRITY = ['..ml..', '..lld.', '..md..', '.lllm.', '.lllmd', 'allmd.', 'l.llm.', '..lmd.', '.llmd.', '.lmld.', '.llmd.', '.lmld.', '.llmd.', '.lmld.', '.llmd.', '.lmlmd', '.llmd.', 'mmmmd.'];
const ARMS = [                                        // the arms of Clement XII under the tiara, a winged Fame on each side
  '............ll', '...........lal', '...........mll', '..........dlll', '.....l...dlmaa', '....lll..dllal', '...llm.l.dlmaa',
  '..llm.lal.dlll', '.llm..lll.dlml', 'llm..mllll.dll', '....mllllll.dm', '..mllllmlll...', 'mllllm..mlllll', 'llllllllllllll',
].map(h => h + [...h].reverse().join(''));
function renderPalace() {
  const p = pix(W, H);
  for (let y = TIP; y < WL; y++) for (let x = PAL[0]; x <= PAL[1]; x++) {
    const v = facadePixel(x, y);
    if (!v) continue;
    p.set(x, y, typeof v === 'string' ? v : pickFlat(v[0], v[1], x, y));
  }
  stampStone(p, ARMS, 226, TIP, 0.62);
  [194, 217, 262, 285].forEach((cx, k) => stampStone(p, ATTIC_STATUES[k & 1], cx - 1, 46, 0.6, k > 1, '#7a5a48'));   // the four gifts of the water
  stampStone(p, ABUNDANCE, 203, 103, 0.56);
  stampStone(p, SALUBRITY, 271, 103, 0.56, true);
  // a few windows are lit behind their curtains; some switch on and off
  const r = rng(404);
  for (const side of [0, 1]) for (let bay = 0; bay < 3; bay++) for (const [wy, wh] of [[77, 11], [97, 16]]) {
    const m0 = 291 + bay * 23 + 7, x = side ? m0 : 479 - m0 - 4, v = r();
    if (v < 0.16) { p.fill(x, wy, 5, wh, '#e8a852'); p.fill(x, wy, 5, 3, '#ffd28a'); p.fill(x + 2, wy, 1, wh, '#8a5634'); }
    else if (v < 0.5) WINS.push({ id: WINS.length, x, y: wy, w: 5, h: wh, n: 2 + Math.floor(r() * 4), off: r() });
  }
  return trim(p);
}
function drawPalaceWindows(t) {
  for (const w of WINS) {
    if (hash(w.id + 20, Math.floor(frac(t / LOOP + w.off) * w.n), 4) >= 0.45) continue;
    rect(ctx, w.x, w.y, w.w, w.h, '#e8a852'); rect(ctx, w.x, w.y, w.w, 3, '#ffd28a'); rect(ctx, w.x + 2, w.y, 1, w.h, '#8a5634');
  }
}

/* ================= The scogliera: carved travertine boulders, the sea horses and their tritons, Oceanus's shell chariot ================= */
const ROCK = ['#2a1e28', '#443230', '#62493c', '#84664c', '#a6865e', '#c4a474', '#dcc08c', '#f0d8a6'];
const ROCK_PTS = [[121, 150], [125, 138], [130, 124], [135, 116], [140, 125], [148, 130], [158, 134], [170, 128], [182, 130], [192, 124],
  [204, 122], [214, 121], [224, 122], [230, 125], [249, 125], [255, 122], [265, 121], [275, 122], [287, 124], [297, 130], [307, 127],
  [319, 133], [331, 130], [340, 122], [346, 125], [352, 137], [358, 150]];
// the boulders: Voronoi cells over a jittered grid, squat like bedded stone; some neighbours are one block (no cleft between)
const RG = { x0: 110, y0: 104, w: 11, h: 7, nx: 24, ny: 8 };
const RSEEDS = [];
{
  const r = rng(35);
  for (let j = 0; j < RG.ny; j++) for (let i = 0; i < RG.nx; i++)
    RSEEDS.push({ id: j * RG.nx + i, x: RG.x0 + (i + 0.1 + r() * 0.8) * RG.w, y: RG.y0 + (j + 0.1 + r() * 0.8) * RG.h, tone: r(), tilt: r() - 0.5 });
}
/** the boulder at a pixel, its nearest neighbour, and the distance to the cleft between them (0 on it) */
function rockCell(x, y) {
  const ci = Math.floor((x - RG.x0) / RG.w), cj = Math.floor((y - RG.y0) / RG.h);
  let d1 = 1e9, d2 = 1e9, s = null, n = null;
  for (let j = cj - 1; j <= cj + 1; j++) for (let i = ci - 1; i <= ci + 1; i++) {
    if (i < 0 || j < 0 || i >= RG.nx || j >= RG.ny) continue;
    const q = RSEEDS[j * RG.nx + i], d = Math.hypot(x - q.x, (y - q.y) * 1.4);
    if (d < d1) { d2 = d1; n = s; d1 = d; s = q; } else if (d < d2) { d2 = d; n = q; }
  }
  const joint = hash(Math.min(s.id, n.id), Math.max(s.id, n.id), 36) > 0.4;          // these two are separate blocks
  return { s, n, e: d2 - d1, joint, cleft: joint && d2 - d1 < 1 && vnoise(x / 3, y / 3, 37) > 0.3 };
}
function rockTop(x) {
  let k = 0;
  while (k < ROCK_PTS.length - 2 && x >= ROCK_PTS[k + 1][0]) k++;
  const [x0, y0] = ROCK_PTS[k], [x1, y1] = ROCK_PTS[k + 1];
  const top = Math.round(lerp(y0, y1, clamp((x - x0) / (x1 - x0), 0, 1)) + (vnoise(x / 4, 0, 31) - 0.5) * 6 + (hash(x, 1, 32) - 0.5) * 1.5);
  return top + (rockCell(x, top + 1).cleft ? 2 : 0);   // a notch where a cleft meets the skyline
}
// The water runs off the shell chariot's scallops onto a ledge, over a ragged lip, and over the lowest lip in the widest fall of
// all; to the sides it spills off the rocks. A fall is made of sheets, a sheet of columns [x, top row, landing row]: rock teeth
// split them, and each lands in foam on the next ledge or the basin.
// teeth: [x, width, rows] notch a sheet's lip; [x, width, rows, from] is a rock that many rows tall that it parts round, from
// rows below its lip.
// fan: rows lower per column out from the middle, rough: how ragged the lip is
function sheet(x0, x1, y0, y1, seed, teeth = [], fan = 0, rough = 3) {
  const cols = [], mid = (x0 + x1) / 2;
  for (let x = x0; x <= x1; x++) {
    const edge = Math.min(x - x0, x1 - x);
    let top = y0 + Math.round((vnoise(x / 2.5, 0, seed) - 0.5) * rough + Math.max(0, Math.abs(x - mid) - 1) * fan) + (edge < 2 ? 2 - edge : 0);
    const bot = Math.min(WL, y1 + Math.round((vnoise(x / 2, 1, seed) - 0.5) * 2));
    let rock = null;
    for (const [tx, tw, th, from = 0] of teeth) if (x >= tx && x < tx + tw) { if (from) rock = [top + from + (x & 1), th]; else top += th; }
    if (rock) { if (rock[0] - 1 > top) cols.push([x, top, rock[0] - 1]); top = rock[0] + rock[1]; }
    if (top < bot - 1) cols.push([x, top, bot]);
  }
  return { x0, x1, y1: Math.max(...cols.map(c => c[2])), cols, seed };
}
const SHELL = { cx: 239.5, top: 119, hy: 113, r: 17.5, ribs: 11, shelf: 134 };   // a fan of flutes from a hinge behind Oceanus's feet
SHELL.A = Math.acos((SHELL.top - SHELL.hy) / SHELL.r);
/** the shell at a pixel: null outside, else [g: 0 on a flute's crest .. 1 in a groove, how far out from the hinge 0..1] */
function shellAt(x, y) {
  if (y < SHELL.top) return null;
  const dx = x - SHELL.cx, dy = y - SHELL.hy, rad = Math.hypot(dx, dy), ang = Math.atan2(dx, dy);
  if (Math.abs(ang) > SHELL.A) return null;
  const g = Math.abs(frac((ang + SHELL.A) / (2 * SHELL.A) * SHELL.ribs) - 0.5) * 2;
  return rad <= SHELL.r - 2 * g * g ? [g, rad / SHELL.r] : null;
}
function shellStreams() {                              // a thread of water from each groove of the scalloped edge down to the ledge
  const cols = [];
  for (let k = 1; k < SHELL.ribs; k++) {
    const a = -SHELL.A + 2 * SHELL.A * k / SHELL.ribs, x = Math.round(SHELL.cx + Math.sin(a) * (SHELL.r - 2));
    let y = SHELL.top; while (shellAt(x, y + 1) || y < SHELL.top + 1) y++;
    const bot = SHELL.shelf + (k & 1);
    cols.push([x, y + 1, bot]);
    if (Math.abs(k - SHELL.ribs / 2) < 2) cols.push([x + (k < SHELL.ribs / 2 ? -1 : 1), y + 2, bot]);
  }
  return { x0: Math.min(...cols.map(c => c[0])), x1: Math.max(...cols.map(c => c[0])), y1: SHELL.shelf + 1, cols, seed: 0, threads: true };
}
const FALLS = [
  shellStreams(),
  sheet(220, 259, 136, 141, 1, [[229, 2, 4], [246, 3, 9]], 0.12, 4),                 // the ledge's ragged lip
  sheet(207, 272, 144, 152, 2, [[216, 2, 3], [238, 1, 2], [261, 2, 5]], 0.08, 5),    // the main fall
  sheet(144, 155, 135, 152, 4, [[148, 3, 3, 6]], 0.35, 7), sheet(170, 182, 134, 152, 6, [[175, 3, 3, 8], [180, 2, 4]], 0.3, 7),
  sheet(198, 204, 140, 152, 7, [], 0.3, 5), sheet(275, 281, 140, 152, 8, [], 0.3, 5),
  sheet(296, 309, 133, 152, 10, [[300, 4, 3, 7]], 0.3, 7), sheet(322, 333, 137, 152, 12, [[326, 3, 2]], 0.35, 7),
];
const HORSE_L = [                                     // the restless sea horse rears, head thrown up, hooves in the air, a wing behind
  '........m...........', '.......llm.......l..', '.....lllllm.....ll..', '...llllslllm...lll..', '..llllllllllm.lllm..', '..ldll..lllllm.llm..',
  '...ll....lllllm.m...', '.........llllllm....', '........llllllllm...', '..ll...lllllllllm...', '.llllllllllllllllm..', '..l..llldllllllllm..',
  '....llldllllllllllm.', '...ll...llllllllllm.', '.........llllllllllm', '..........lllllllllm',
];
const HORSE_R = [                                     // the calm one walks on, neck arched, head low (drawn facing left, stamped flipped)
  '...........m........', '..........llm...l...', '........llllllm.ll..', '......lllllllllmll..', '.....llsllllllllm...', '....lllll.lllllllm..',
  '...llll....llllllm..', '...ldl.....lllllllm.', '....l.....llllllllm.', '.........lllllllllm.', '.....ll.llllllllllm.', '....llllldlllllllllm',
  '....l...llllllllllm.', '........llllllllllm.', '.........lllllllllm.',
];
const TRITON_L = ['..ll....', '.llll..l', '.lll..l.', '..l..l..', '.lllll..', 'llllll..', 'l.llll..', 'l.lll...', '..lll...', '.llll...', '.lllll..'];   // he hauls on the bridle
const TRITON_R = ['..ll....', '.llllaa.', '.llll...', '..ll....', '.llll...', 'lllll...', 'l.lll...', 'l.lll...', '..lll...', '.llll...', '.lllll..'];   // he sounds a conch (stamped flipped)
const VASE = ['.lllll.', 'lallmmd', '.llmmd.', '..lmd..', '.llmmd.', 'lallmmd', 'lllmmdd', '.llmmd.', '..lmd..'];   // the "ace of cups"
function rockPixel(p, x, y, d) {                      // d: rows below the rock's top edge
  const { s, n, e, joint, cleft } = rockCell(x, y), lit = 1 - 0.25 * ((x - 239.5) / 120) ** 2;
  let L = (0.4 + 0.28 * s.tone) * lit + 0.14 * clamp((y - s.y) / 5, -1, 1) + s.tilt * (x - s.x) * 0.03
    + (vnoise(x / 2, y / 1.5, 33) - 0.5) * 0.14 + 0.18 * smooth(WL - 15, WL, y) + (d === 0 ? 0.24 : d === 1 ? 0.08 : 0);
  if (cleft) L = 0.1 + (d === 0 ? 0.3 : 0);
  else if (joint && e < 2.4) L += n.y > s.y ? 0.12 : -0.12;                          // a lit lip over a cleft, the shade under one
  let col = pick(ROCK, L, x, y);
  const wet = smooth(WL - 9, WL, y) * 0.34;          // the water's light on the rocks' feet
  if (wet > 0.02) col = mixHex(col, '#58d8c8', Math.round(wet * 8) / 8);
  p.set(x, y, col);
}
const FALL_C = ['#1e4652', '#2a6a70', '#4a9a94', '#86d0c2', '#c4f0e4', '#eefffa'];
function renderFalls(p) {                             // the wet sheets behind the streaks: a lip over each, foam where each lands
  for (const f of FALLS) {
    const xs = new Set(f.cols.map(c => c[0]));
    for (const [x, top, bot] of f.cols) {
      const strand = hash(x, f.seed, 35), edge = !xs.has(x - 1) || !xs.has(x + 1);
      if (!f.threads) { p.set(x, top - 1, pick(ROCK, 0.22, x, top - 1)); p.set(x, top - 2, pick(ROCK, 0.76, x, top - 2)); }   // the lip it pours over
      for (let y = top; y <= bot; y++) {
        if (edge && !f.threads && y > top && bayer(x, y) > 0.62) continue;             // the rock shows through where a sheet thins
        const v = y === top ? 0.9 : 0.18 + 0.28 * strand + 0.36 * (y - top) / (bot - top) + (hash(x, y >> 1, 36) - 0.5) * 0.3;
        p.set(x, y, pick(FALL_C, v, x, y));
      }
      for (const dx of [-1, 0, 1]) if (bayer(x + dx, bot) < 0.7) p.set(x + dx, bot, bayer(x + dx, bot) < 0.3 ? '#ffffff' : '#c8f4ea');   // foam
    }
  }
}
function renderShell(p) {                             // the broad fluted shell, its scalloped edge, the shadow under it
  for (let y = SHELL.top - 1; y <= SHELL.hy + SHELL.r + 2; y++) for (let x = Math.floor(SHELL.cx - SHELL.r) - 1; x <= SHELL.cx + SHELL.r + 1; x++) {
    const s = shellAt(x, y);
    if (!s) { if (y > SHELL.top && (shellAt(x, y - 1) || shellAt(x, y - 2))) p.set(x, y, shellAt(x, y - 1) ? ROCK[0] : pick(ROCK, 0.2, x, y)); continue; }
    const [g, rr] = s;
    let L = 0.4 + 0.3 * (1 - g) + 0.12 * rr - (g > 0.72 ? 0.2 : 0);
    if (y === SHELL.top) L = 0.88 - (g > 0.72 ? 0.24 : 0);
    else if (y === SHELL.top + 1) L -= 0.16;                                           // under the rim's lip
    p.set(x, y, pick(MARBLE, L, x, y));
  }
}
function renderRocks(p) {
  for (let x = 121; x <= 358; x++) { const top = rockTop(x); for (let y = top; y < WL + 1; y++) rockPixel(p, x, y, y - top); }
  stampStone(p, HORSE_L, 193, 117, 0.68, false, '#4a3430');
  stampStone(p, HORSE_R, 267, 118, 0.68, true, '#4a3430');
  stampStone(p, TRITON_L, 183, 125, 0.64, false, '#4a3430');
  stampStone(p, TRITON_R, 289, 125, 0.64, true, '#4a3430');
  for (const [x0, x1] of [[182, 213], [266, 297]]) for (let x = x0; x <= x1; x++) {     // the rocks they rise out of
    const top = 131 - Math.round(hash(x >> 1, 5, 38) * 2 + vnoise(x / 3, 0, 39) * 3);
    for (let y = top; y <= 136; y++) rockPixel(p, x, y, y - top);
  }
  stampStone(p, VASE, 342, 113, 0.6, false, '#3a2830');
  renderShell(p);
  renderFalls(p);
  stampStone(p, OCEANUS, 230, 98, 0.6, false, '#2a1a24');
}

/* ================= The basin, its rim, the terrace and the steps ================= */
const WATER = ['#062432', '#0a3c4a', '#0e5a68', '#147e86', '#20a4a0', '#44c6b6', '#82e2cc', '#c4f8e8'];
const BASALT = ['#0c0c16', '#13131e', '#1a1926', '#23202e', '#2d2834', '#3a3038', '#4c3c3a', '#624c40', '#7c5e48', '#9a7450'];
const TRAVG = ['#221a28', '#342832', '#4a3a3e', '#64504a', '#806858', '#9c8468', '#b8a07c', '#d2bc92'];
const UW_LAMPS = [[140, 157], [170, 158], [202, 158], [240, 159], [278, 158], [310, 158], [340, 157], [160, 172], [240, 174], [320, 172],
  [92, 187], [180, 188], [300, 188], [388, 187]];     // lamps under the water
const basinL = y => lerp(BASIN.l0, BASIN.l1, (y - WL) / (RIM - WL));
const basinR = y => lerp(BASIN.r0, BASIN.r1, (y - WL) / (RIM - WL));
const inBasin = (x, y) => y >= WL && y < RIM && x >= basinL(y) && x <= basinR(y);
const wallGround = x => VP.y + (SIDE.ground - VP.y) * (VP.x - mirW(x)) / (VP.x - SIDE.far);   // the side walls' foot at column x
const mirW = x => x > CX ? 479 - x : x;
function groundGlow(x, y) {                           // [the basin's aqua, warm shop and lamp light]
  // the water lights the paving round it and fades with the distance from its edge: out over the terrace from the near
  // rim, and out over the side walkways from the side rims, which stand in its glow themselves
  const near = 0.8 * Math.max(0, 1 - (y - RIM) / 46) * Math.max(0, 1 - Math.abs(x - CX) / 290);
  const side = y < RIM ? (x < CX ? basinL(y) - x : x - basinR(y)) : 0;
  const aq = y >= RIM ? near : side <= 4 ? near + 0.5 : 0.8 * Math.max(0, 1 - (side - 4) / 46);
  const pool = (cx, cy, rx, ry, s) => s * Math.max(0, 1 - Math.hypot((x - cx) / rx, (y - cy) / ry));
  const warm = pool(30, 188, 84, 34, 0.7) + pool(450, 188, 84, 34, 0.6) + pool(70, 236, 90, 30, 0.3) + pool(410, 236, 90, 30, 0.3) + pool(240, 250, 200, 40, 0.16);
  return [aq, warm];
}
function renderGround(p) {
  for (let y = WL - 2; y < H; y++) for (let x = 0; x < W; x++) {
    if (p.alpha(x, y) && y <= WL) continue;                            // the rocks' feet
    if (inBasin(x, y)) {                                                // still water: bright over the lamps, deeper towards the rim
      let v = 0.5 - 0.3 * (y - WL) / (RIM - WL) + (vnoise(x / 7, y / 3, 41) - 0.5) * 0.1;
      for (const [lx, ly] of UW_LAMPS) v += 0.3 * Math.max(0, 1 - Math.hypot((x - lx) / 26, (y - ly) / 9));
      p.set(x, y, pick(WATER, v, x, y));
      continue;
    }
    const [aq, warm] = groundGlow(x, y);
    const dl = basinL(y) - x, dr = x - basinR(y);
    let col;
    if (y >= WL && y < RIM && ((dl > 0 && dl <= 4) || (dr > 0 && dr <= 4))) {                 // side rims
      const d = dl > 0 ? dl : dr;
      col = pick(TRAVG, d <= 2 ? 0.78 : 0.4, x, y);
    } else if (y >= RIM && y < TERR && x >= BASIN.l1 - 4 && x <= BASIN.r1 + 4) {               // the near rim: lit top, shaded face
      col = y < RIM + 2 ? pick(TRAVG, 0.86 - (hash(x >> 3, 1, 42) < 0.3 ? 0.1 : 0), x, y) : x % 13 === 0 ? TRAVG[0] : pick(TRAVG, 0.3 - (y - RIM) * 0.03 + warm * 0.2, x, y);
    } else if (y >= STEPS[0]) {                                                                // the steps down from where we stand
      let k = 0; while (k < STEPS.length - 1 && y >= STEPS[k + 1]) k++;
      const d = y - STEPS[k];
      let L = 0.46 - k * 0.07 - d * 0.008 + warm * 0.3 + aq * 0.25 + (vnoise(x / 9, y / 2, 43) - 0.5) * 0.1;
      if (d === 0) L += 0.26; else if (d === 1) L += 0.1;
      if (y === STEPS[k + 1] - 1) L -= 0.16;
      if ((x + k * 17) % 41 === 0 && d > 1) L -= 0.1;                                          // joints between the slabs
      col = pick(TRAVG, L, x, y);
    } else if (y >= wallGround(x) || (x > SIDE.far && x < 479 - SIDE.far)) {                   // sampietrini on the walkways and the terrace
      const row = y >> 1, cob = ((x + (row & 1)) >> 1);
      let L = 0.3 + warm * 0.5 + aq * 0.3 + (hash(cob, row, 44) - 0.5) * 0.16;
      if ((y & 1) === 0 && ((x + (row & 1)) & 1) === 0) L += 0.07;
      col = pick(BASALT, L, x, y);
    } else continue;
    if (aq > 0.04) col = mixHex(col, '#40d8c8', Math.round(Math.min(0.3, aq * 0.3) * 10) / 10);
    p.set(x, y, col);
  }
}

/* ================= The piazza's side walls, in perspective: ochre houses with shops (west), rose ones with the bar (east) ================= */
const OCHRE = ['#241622', '#36202a', '#4e2e2e', '#6a4032', '#8a5638', '#a86e40', '#c48a4c', '#daa45a'];
const ROSA = ['#261624', '#3a1e2a', '#522a32', '#6e3a3a', '#8c4c44', '#a8624e', '#c07a5a', '#d49468'];
const SHUT = ['#11261c', '#1c3a2a', '#2a5238', '#3e6e48', '#56885a'];
const TRIM = ['#3a2e34', '#5a4a44', '#8a7460', '#b89c7c', '#d8bc94'];
const GLOW = ['#a85424', '#e08638', '#ffb058', '#ffd690', '#fff0c8'];
/** wall coordinates of a pixel on the west wall (the east wall: mirror the column first): A px along the wall from its far
    end, hgt px above the ground, both at the far end's scale, where k = 1 */
function wallAt(mx, y) {
  const k = (VP.x - mx) / (VP.x - SIDE.far);
  return { k, A: 156 * (1 - 1 / k), hgt: (VP.y - y) / k + (SIDE.ground - VP.y) };
}
/** and back: the screen position of a point on the wall */
function wallXY(A, hgt, east) {
  const k = 1 / (1 - A / 156), x = VP.x - k * (VP.x - SIDE.far);
  return [east ? 479 - x : x, VP.y - (hgt - (SIDE.ground - VP.y)) * k];
}
// shops along each wall's ground floor: a0..a1 along the wall, an awning's two colours, the light inside, what is on show
const SHOPS = [
  [{ a0: 4, a1: 24, kind: 'cards', cool: true }, { a0: 33, a1: 49, awn: ['#c83a30', '#f6eee2'], kind: 'pizza' }, { a0: 55, a1: 72, awn: ['#e87aa8', '#f6eee2'], kind: 'gelato' }],
  [{ a0: 4, a1: 24, awn: ['#2a5a3e', '#e8dcc0'], kind: 'bar' }, { a0: 30, a1: 46, kind: 'tabacchi', cool: true }, { a0: 52, a1: 72, awn: ['#c8a040', '#6a1c20'], kind: 'pizza' }],
];
const FLOORS = [27, 47, 67];                           // sill height of each floor's windows; they stand every 17 px along the wall
const winState = id => { const h = hash(id, 7, 61); return h < 0.3 ? 'shut' : h < 0.48 ? 'lit' : h < 0.74 ? 'toggle' : 'dark'; };
const SIDE_WINS = [];                                  // toggling windows: { id, spr, x, y, n, off }
function wallPixel(mx, y, east, lit) {
  const { k, A, hgt } = wallAt(mx, y);
  if (hgt > 91 || hgt < 0) return null;
  const ramp = east ? ROSA : OCHRE, x = east ? 479 - mx : mx;
  let L = 0.2 + 0.24 * (1 / k - 0.56) / 0.44 + 0.3 * smooth(40, 2, hgt) + (vnoise(A / 5, hgt / 5, east ? 52 : 51) - 0.5) * 0.14;
  if (lit === undefined) {
    if (hgt > 88) return (Math.floor(A * 1.3) & 1) ? '#3a1a16' : hgt > 90 ? '#8a4a34' : '#6a3226';       // roof tiles at the eave
    if (hgt > 85.5) return pick(TRIM, L + 0.3, x, y);
    if (hgt < 21) {
      const shop = SHOPS[east ? 1 : 0].find(s => A >= s.a0 && A < s.a1);
      if (shop && shop.awn && hgt >= 15 && hgt < 20) return shop.awn[Math.floor(A / 2.4) & 1];
      if (shop && hgt >= 0.6 && hgt < (shop.awn ? 15 : 17)) {
        if (A < shop.a0 + 0.9 || A > shop.a1 - 1.1) return '#2a1c1e';
        if (hgt < 6 && hgt >= 2.5) {                    // the counter and what is on it
          const g = Math.floor(A / 1.6), h = hash(g, east ? 3 : 4, 62);
          if (shop.kind === 'gelato') return ['#ff8ab0', '#f6eccc', '#8cc060', '#6a3a22', '#ffd040', '#c8a0e0'][Math.floor(h * 6)];
          if (shop.kind === 'pizza') return h < 0.5 ? '#d8502a' : '#f0c060';
          if (shop.kind === 'cards') return ['#e04a4a', '#4a8ae0', '#f0d050', '#f4f0e8', '#50b070'][Math.floor(h * 5)];
          return h < 0.4 ? '#3a2418' : '#6a3a1e';
        }
        if (hgt < 2.5) return shop.cool ? '#5a5e70' : '#5a2e1a';
        if (shop.kind === 'bar' && Math.abs(hgt - 10) < 0.6) return (Math.floor(A * 1.2) & 1) ? '#2a5a2a' : '#8a1a1a';   // bottles on a shelf
        if (shop.kind === 'cards' && hgt > 7 && (Math.floor(A / 1.4) & 1) && (Math.floor(hgt / 1.6) & 1)) return ['#e04a4a', '#4a8ae0', '#f0d050', '#50b070'][Math.floor(hash(Math.floor(A / 1.4), Math.floor(hgt / 1.6), 63) * 4)];
        const v = 0.3 + 0.6 * (hgt / 15);
        return shop.cool ? pick(['#8a90a8', '#c8d0e0', '#eef2ff'], v, x, y) : pick(GLOW, v, x, y);
      }
      return pick(TRIM, L - 0.12 + ((Math.floor(hgt / 3.5) & 1) ? 0.04 : 0), x, y);                       // stone plinth and piers
    }
    if (hgt < 23) return pick(TRIM, L + 0.2, x, y);                                                      // string course
  }
  const fl = FLOORS.findIndex(h0 => hgt >= h0 - 1.2 && hgt < h0 + 15.5);
  if (fl >= 0) {
    const col = Math.floor((A + 6) / 17), wa = A + 6 - col * 17, wy = hgt - FLOORS[fl], id = (east ? 100 : 0) + fl * 10 + col, st = winState(id);
    if (wa >= 5 && wa < 11) {
      if (lit !== undefined) return lit === id && wy >= 0 && wy < 14 ? (wa > 7.6 && wa < 8.4 ? '#8a5436' : wy > 10 ? '#ffd08a' : '#f4a852') : null;
      if (wy < 0) return pick(TRIM, L + 0.36, x, y);
      if (wy >= 14) return pick(TRIM, L + 0.22, x, y);
      if (st === 'shut') return (Math.floor(hgt * 1.4) & 1) ? SHUT[0] : pick(SHUT, L + 0.3, x, y);
      if (st === 'lit') return wa > 7.6 && wa < 8.4 ? '#8a5436' : wy > 10 ? '#ffd08a' : '#f4a852';
      return wa > 7.6 && wa < 8.4 ? '#231a22' : '#130e16';
    }
    if (lit === undefined && st !== 'shut' && wy >= 0 && wy < 14 && ((wa >= 2 && wa < 5) || (wa >= 11 && wa < 14)))
      return (Math.floor(hgt * 1.4) & 1) ? SHUT[0] : pick(SHUT, L + 0.34, x, y);                          // shutters folded back
  }
  return lit === undefined ? pick(ramp, L, x, y) : null;
}
function renderSides(p) {
  for (const east of [false, true]) {
    for (let mx = 0; mx <= SIDE.far; mx++) for (let y = 0; y < 172; y++) {
      const c = wallPixel(mx, y, east);
      if (c) p.set(east ? 479 - mx : mx, y, c);
    }
    // the streets that leave the piazza beside the palace: a dim house front far down each
    for (let x = SIDE.far + 1; x < PAL[0]; x++) for (let y = 70; y < WL; y++) {
      const X = east ? 479 - x : x, wy = (y - 78) % 16, wx = (x - SIDE.far) % 8;
      let col = pick(east ? ROSA : OCHRE, 0.16 + 0.2 * smooth(100, 150, y), X, y);
      if (y === 70) col = '#5a2c22';
      else if (y >= 78 && y < 142 && wy < 8 && wx >= 2 && wx < 6) col = hash(wx > 3 ? 1 : 0, Math.floor((y - 78) / 16), east ? 65 : 64) < 0.3 ? '#e8a852' : '#15101a';
      p.set(X, y, col);
    }
  }
}
function renderSideLit(east, id) {                     // the lit glass of one toggling window
  const p = pix(W, 172);
  for (let mx = 0; mx <= SIDE.far; mx++) for (let y = 0; y < 172; y++) { const c = wallPixel(mx, y, east, id); if (c) p.set(east ? 479 - mx : mx, y, c); }
  return trim(p);
}
// hanging signs, face on to the piazza (generic words only): [x, y, text, back, ink]
const SIGNS = [[3, 116, 'GELATI', '#ffe6f0', '#c8327a'], [42, 119, 'PIZZA', '#2a1a14', '#ffb040'], [381, 121, 'BAR', '#14301e', '#f4e4c0']];
const TSIGN = { x: 419, y: 115 };                      // the tobacconist's T
function renderSigns(p) {
  for (const [x, y, s, back, ink] of SIGNS) {
    const w = textWidth(s) + 4;
    p.fill(x, y, w, 9, back); p.fill(x, y, w, 1, '#1a1216'); p.fill(x, y + 8, w, 1, '#1a1216');
    p.fill(x + (w >> 1), y - 3, 1, 3, '#1a1216');
    textP(p, s, x + 2, y + 2, ink);
  }
  p.fill(TSIGN.x, TSIGN.y, 9, 11, '#141a3a'); p.fill(TSIGN.x + 2, TSIGN.y + 2, 5, 2, '#f4f4ff'); p.fill(TSIGN.x + 4, TSIGN.y + 4, 1, 5, '#f4f4ff');
  p.fill(TSIGN.x + 4, TSIGN.y - 3, 1, 3, '#1a1216');
}

/* ================= Santi Vincenzo e Anastasio: the corner of its front of clustered columns, at the right edge ================= */
// It stands nearer than the terrace: its wall comes down to the steps at row 226 (CHURCH_FOOT), so whoever walks out
// behind it at the right edge (the terrace's lanes, the east side street's walkway) goes behind its columns, not over them.
const CHURCH_X = 447, CHURCH_FOOT = 226;
function renderChurch(p) {
  const shaft = (x0, w, y0, y1) => { for (let y = y0; y < y1; y++) for (let i = 0; i < w; i++) {
    const u = i / (w - 1), L = 0.5 + 0.2 * smooth(40, 200, y) + (u < 0.2 ? 0.2 : u < 0.45 ? 0.1 : u < 0.7 ? -0.04 : -0.2);
    p.set(x0 + i, y, pickFlat(STONE, L, x0 + i, y));
  } };
  for (let y = 0; y < CHURCH_FOOT; y++) for (let x = CHURCH_X; x < W; x++) p.set(x, y, pick(STONE, 0.2 + 0.14 * smooth(60, 210, y) + (x < CHURCH_X + 2 ? 0.16 : 0) + (vnoise(x / 4, y / 6, 72) - 0.5) * 0.06, x, y));   // the wall behind
  shaft(452, 6, 0, 24); shaft(466, 8, 0, 22);                                 // the upper order
  shaft(451, 8, 58, 196); shaft(465, 10, 52, 204);                            // the lower order: free-standing Corinthian columns
  for (const [x0, w, y0] of [[450, 10, 48], [464, 12, 40]]) for (let y = y0; y < y0 + 11; y++) for (let i = 0; i < w; i++)
    p.set(x0 + i, y, pick(STONE, 0.62 + (hash(x0 + i, y, 73) < 0.42 ? -0.22 : 0.08) - i / w * 0.2, x0 + i, y));   // capitals
  for (const [x0, w, y0, y1] of [[449, 12, 196, 216], [462, 16, 204, 228]]) for (let y = y0; y < y1; y++) for (let i = 0; i < w; i++)
    p.set(x0 + i, y, pickFlat(STONE, 0.5 + (y === y0 || y === y1 - 1 ? 0.2 : y === y0 + 1 ? -0.12 : 0) + (i === 0 ? 0.14 : i === w - 1 ? -0.2 : 0), x0 + i, y));   // pedestals
  for (let y = 24; y < 40; y++) for (let x = 445; x < W; x++) {               // the heavy entablature between the two orders
    const d = y - 24;
    p.set(x, y, pickFlat(STONE, 0.5 + (d === 15 ? 0.3 : d === 14 ? 0.1 : d > 9 ? -0.08 : d === 9 ? 0.22 : d < 3 ? 0.12 : -0.02) - (x - 445) * 0.004, x, y));
  }
}

/* ================= The east wing's restoration scaffold and its hoarding: CAFFE LUPA, a made-up coffee ================= */
const SCAF_X = [288, 311, 335];                        // standards (the wing shows through the tubes)
function renderScaffold() {
  const p = pix(W, H), tube = y => y < 100 ? '#a0a0ac' : '#74748a';
  const put = (x, y, c) => { if (y < rockTop(x) - 1) p.set(x, y, c); };       // it stands behind the rocks
  for (const x of SCAF_X) for (let y = 43; y < 152; y++) put(x, y, y % 13 === 0 ? '#3a3a46' : tube(y));
  for (const y of [45, 68, 95, 121, 144]) for (let x = 286; x <= 337; x++) put(x, y, tube(y));
  for (const y of [96, 122]) for (let x = 287; x <= 336; x++) { put(x, y, x % 9 === 0 ? '#3a2616' : '#8a6238'); put(x, y + 1, '#2a1c14'); }   // plank decks
  for (const y of [110, 136]) for (let x = 289; x <= 334; x += 2) put(x, y, '#5c5c6c');                                            // guard rails
  for (const [x0, y0, x1, y1] of [[288, 144, 311, 122], [311, 144, 335, 122], [311, 121, 335, 96], [288, 68, 311, 45]]) {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let s = 0; s <= n; s++) put(Math.round(lerp(x0, x1, s / n)), Math.round(lerp(y0, y1, s / n)), '#686878');
  }
  for (let y = 98; y < 121; y++) { put(326, y, '#b8a878'); put(329, y, '#8a7a50'); if (y % 3 === 0) { put(327, y, '#9a8a5c'); put(328, y, '#9a8a5c'); } }   // a ladder
  for (let x = BB.x - 1; x <= BB.x + BB.w; x++) { p.set(x, BB.y - 1, '#1a1216'); p.set(x, BB.y + BB.h, '#2a2024'); p.set(x, BB.y + BB.h + 1, '#141014'); }
  for (let y = BB.y - 1; y <= BB.y + BB.h; y++) { p.set(BB.x - 1, y, '#241a1e'); p.set(BB.x + BB.w, y, '#141014'); }
  for (const lx of BB_LAMPS) { p.fill(lx, 64, 1, 5, '#3a3a46'); p.fill(lx - 1, 69, 3, 2, '#22222c'); }
  p.fill(BB.x, BB.y, BB.w, BB.h, '#0d0f24');           // behind the face (the ad or the pitch covers it)
  return trim(p);
}
const AD_RED = ['#3c0a14', '#5a0e1a', '#7c1420', '#9e1c26', '#bc2a2e', '#d23c38'];
const CUP = ['.wwwwwww..', 'wkkkkkkkw.', 'wWWWWWWWww', 'wWWWWWWw.w', '.wWWWWWwww', '.wWWWWWw..', '..wwwww...', 'ssssssssss'];
function renderAd() {
  const p = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) {
    const v = 0.2 + BB_LAMPS.reduce((s, lx) => s + 0.55 * Math.max(0, 1 - Math.hypot((BB.x + i - lx) / 16, (j + 2) / 22)), 0);
    const f = clamp(v, 0, 1) * (AD_RED.length - 1); let k = Math.floor(f); if (f - k > 0.5 + (bayer(i, j) - 0.5) * 0.3) k++;   // flat printed bands
    p.set(i, j, AD_RED[Math.min(k, AD_RED.length - 1)]);
  }
  for (let i = 1; i < BB.w - 1; i++) { p.set(i, 1, '#e8c070'); p.set(i, BB.h - 2, '#b8904a'); }
  for (let j = 1; j < BB.h - 1; j++) { p.set(1, j, '#e8c070'); p.set(BB.w - 2, j, '#b8904a'); }
  CUP.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const c = { w: '#fff6e4', W: '#e8dcc6', k: '#2a120c', s: '#f0e2c8' }[row[i]]; if (c) p.set(3 + i, 9 + j, c); } });
  textP(p, 'CAFFE', 17, 3, '#ffd88a');
  textP(p, 'LUPA', 14, 9, '#2a060c', 2); textP(p, 'LUPA', 13, 8, '#fff2d8', 2);
  return p.done();
}
const BB_FLICK = [[91.3, 92.6], [233.4, 234.5]];       // the right lamp stutters now and then
function drawBillboard(t, pitch) {
  blit(SCAF_C);
  const off = inEvent(t, BB_FLICK) && hash(77, Math.floor(t * 18), 2) < 0.5, hum = 0.95 + 0.05 * wave(t, 1500, 0.2);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C, BB.x, BB.y);
    for (let k = 0; k < 3; k++) {                      // steam off the cup
      const { u } = cycle(t, 60, k / 3), sx = BB.x + 5 + k * 2 + Math.round(Math.sin(u * 7 + k * 2) * 1.2);
      ctx.globalAlpha = 0.75 * Math.sin(Math.PI * u); rect(ctx, sx, BB.y + 8 - Math.round(u * 6), 1, 1, '#fff6e4');
    }
    ctx.globalAlpha = 1;
    const sh = frac(t / 12);                           // a gloss runs across the print
    if (sh < 0.1) { ctx.globalAlpha = 0.22; for (let j = 2; j < BB.h - 2; j++) { const sx = Math.round(lerp(-8, BB.w + 8, sh / 0.1) - j * 0.6); if (sx > 1 && sx < BB.w - 4) rect(ctx, BB.x + sx, BB.y + j, 3, 1, '#ffffff'); } ctx.globalAlpha = 1; }
    if (off) { ctx.globalAlpha = 0.35; rect(ctx, BB.x + 22, BB.y, 22, BB.h, '#12040a'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {
    if (off && k === 1) return;
    rect(ctx, lx - 1, 71, 3, 1, '#fff4dc');
    halo(HL.bbLamp, lx, 72, hum);
  });
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + 8, (off ? 0.7 : 1) * hum);
}

/* ================= Wall lanterns on their brackets, moths round the west one ================= */
const LANTS = [{ x: 76, y: 117, dir: 1 }, { x: 402, y: 115, dir: -1 }];
function renderLanterns(p) {
  for (const l of LANTS) {
    for (let k = 1; k <= 5; k++) p.set(l.x - l.dir * k, l.y - 5, '#1a1216');
    p.fill(l.x - 1, l.y - 5, 3, 1, '#1a1216'); p.fill(l.x - 2, l.y - 4, 5, 1, '#2a2024'); p.fill(l.x - 2, l.y + 2, 5, 1, '#2a2024'); p.set(l.x, l.y + 3, '#1a1216');
  }
}
const MOTHS = Array.from({ length: 7 }, (_, i) => ({ k: 37 + i * 13, k2: 53 + i * 17, ph: i * 0.37, r: 4 + (i % 3) * 2.5 }));
function drawLanterns(t) {
  LANTS.forEach((l, i) => {
    const lv = 0.94 + 0.06 * wave(t, 1300 + i * 400, i * 0.3);
    rect(ctx, l.x - 2, l.y - 3, 5, 5, '#ffe2a4'); rect(ctx, l.x - 1, l.y - 2, 3, 3, '#fff6dc'); rect(ctx, l.x, l.y - 3, 1, 5, '#6a4a30');
    halo(HL.lamp, l.x, l.y, lv); halo(HL.lampCore, l.x, l.y - 1, lv);
  });
  const l = LANTS[0];
  for (const m of MOTHS) {
    const x = l.x + Math.sin(TAU * (m.k * t / LOOP + m.ph)) * m.r + Math.sin(TAU * (m.k2 * 3 * t / LOOP)) * 1.5, y = l.y - 1 + Math.cos(TAU * (m.k2 * t / LOOP + m.ph)) * m.r * 0.7;
    rect(ctx, Math.round(x), Math.round(y), 1, 1, hash(m.k, step(t, 1920)) < 0.5 ? '#fff4d0' : '#c8a878');
  }
}

/* ================= Water that never stops: the falls, their foam and rings, the basin's shimmer, the spray ================= */
const STREAKS = [];                                    // bright streaks running down every column of every fall
{
  const r = rng(91);
  for (const f of FALLS) for (const [x, top, bot] of f.cols) for (let k = 0; k < (bot - top > 10 ? 3 : 2); k++)
    STREAKS.push({ x, y0: top, y1: bot === WL ? WL + 1 : bot, n: 150 + Math.floor(r() * 130), ph: r(), len: 2 + Math.floor(r() * 3), a: 0.5 + r() * 0.5 });
}
function drawFalls(t) {
  ctx.fillStyle = '#f4fffb';
  for (const s of STREAKS) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q), y = Math.round(s.y0 + u * u * (s.y1 - s.y0));
    ctx.globalAlpha = s.a * (0.45 + 0.55 * u);
    ctx.fillRect(s.x, y, 1, Math.min(s.len + (u > 0.6 ? 1 : 0), s.y1 - y + 1));
  }
  const st = step(t + 0.03, 2400);                     // foam where each sheet lands
  FALLS.forEach((f, fi) => {
    for (const [x, , yb] of f.cols) {
      const h = hash(x, st, 80 + fi);
      if (h < 0.62) { ctx.globalAlpha = 0.55 + 0.45 * h; ctx.fillRect(x + (h < 0.08 ? -1 : h > 0.54 ? 1 : 0), yb - (h < 0.2 ? 1 : 0), 1, 1); }
      if (yb === WL && hash(x, st >> 1, 90 + fi) < 0.3) { ctx.globalAlpha = 0.6; ctx.fillRect(x + (h < 0.3 ? -1 : 1), WL + 1 + Math.floor(hash(x, st, 3) * 3), 1, 1); }
    }
  });
  ctx.globalAlpha = 1;
}
const CAUSTICS = [], SPARKS = [], COINS_IN = [];
{
  const r = rng(92);
  for (let i = 0; i < 90; i++) { const y = WL + 3 + Math.floor(r() * (RIM - WL - 5)); CAUSTICS.push({ x: lerp(basinL(y) + 6, basinR(y) - 12, r()), y, len: 3 + Math.floor(r() * 6), k: 20 + Math.floor(r() * 40), k2: 9 + Math.floor(r() * 20), ph: r() }); }
  for (let i = 0; i < 34; i++) { const y = WL + 2 + Math.floor(r() * (RIM - WL - 3)); SPARKS.push({ x: Math.round(lerp(basinL(y) + 3, basinR(y) - 3, r())), y, k: 30 + Math.floor(r() * 70), ph: r() }); }
  for (let i = 0; i < 60; i++) { const y = 170 + Math.floor(r() * 24); COINS_IN.push({ x: Math.round(lerp(basinL(y) + 8, basinR(y) - 8, r())), y, k: 12 + Math.floor(r() * 30), ph: r() }); }   // coins on the bottom
}
function drawBasin(t) {
  reflect(ctx, t, { top: WL + 1, rows: 30, squash: 2.3, alpha: 0.3, x: 104, w: 272, amp: 0.8, grow: 0.07 });   // the lit front, mirrored
  for (const [lx, ly] of UW_LAMPS) halo(HL.uw, lx, ly, 0.6 + 0.2 * wave(t, 40, lx * 0.01));
  for (let k = 0; k < 3; k++) {                        // rings spreading from the big falls
    const { u } = cycle(t, 90, k / 3), y = WL + 3 + Math.round(u * 15), hw = 28 + u * 30;
    ctx.fillStyle = '#d8fff4'; ctx.globalAlpha = (1 - u) * 0.5;
    for (let dx = -hw; dx <= hw; dx += 4) ctx.fillRect(Math.round(CX + dx + (k & 1) * 2), y + Math.round(2.5 * (1 - (dx / hw) ** 2)), 2, 1);
  }
  ctx.fillStyle = '#bff6ea';
  for (const c of CAUSTICS) {                          // caustics wandering over the bottom
    const a = 0.12 + 0.3 * Math.max(0, wave(t, c.k, c.ph));
    if (a < 0.14) continue;
    ctx.globalAlpha = a; ctx.fillRect(Math.round(c.x + 5 * wave(t, c.k2, c.ph * 3)), c.y, c.len, 1);
  }
  for (const c of COINS_IN) { const g = wave(t, c.k, c.ph); if (g > 0.2) { ctx.globalAlpha = 0.25 + 0.6 * g; ctx.fillStyle = g > 0.92 ? '#fffbe0' : '#e8c060'; ctx.fillRect(c.x, c.y, 1, 1); } }
  ctx.fillStyle = '#ffffff';
  for (const s of SPARKS) { const g = wave(t, s.k, s.ph); if (g > 0.75) { ctx.globalAlpha = (g - 0.75) * 4; ctx.fillRect(s.x, s.y, 1, 1); } }
  ctx.globalAlpha = 1;
}
// spray: a pale veil over the big falls, fine drops drifting off it over the basin
let PUFFS;
function makePuff(r, col) {
  const s = r * 2 + 1, p = pix(s, s), c = hex(col);
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) { const d = Math.hypot(x - r, y - r) / (r + 0.5); if (d < 1 && (1 - d) * 1.6 > bayer(x, y)) p.set(x, y, c); }
  return p.done();
}
const MIST = Array.from({ length: 14 }, (_, i) => ({ n: 16 + (i * 7) % 9, ph: i * 0.618 % 1, x: 212 + (i * 37) % 56, k: 11 + i * 3 }));
const DROPS = Array.from({ length: 46 }, (_, i) => ({ id: i, n: 40 + (i * 13) % 30, ph: (i * 0.377) % 1 }));
function drawSpray(t, depth) {
  if (depth === 0) {                                   // far: the veil rising in front of the niche
    for (const m of MIST) {
      const { u } = cycle(t, m.n, m.ph), r = 2 + Math.floor(u * 4);
      ctx.globalAlpha = 0.2 * Math.sin(Math.PI * u);
      ctx.drawImage(PUFFS[r], Math.round(m.x + 6 * wave(t, m.k, m.ph) + u * 8) - r, Math.round(150 - u * 26) - r);
    }
  } else {                                             // mid: drops hopping off the falls; near: spray drifting over the rim
    ctx.fillStyle = depth === 1 ? '#e8fff8' : '#c8f4ea';
    for (const d of DROPS) {
      if ((d.id % 3 === 0) !== (depth === 2)) continue;
      const { cyc, u } = cycle(t, d.n, d.ph), h1 = hash(d.id, cyc, 21), h2 = hash(d.id, cyc, 22);
      if (depth === 1) {
        const f = FALLS[Math.floor(h1 * FALLS.length)], x0 = lerp(f.x0, f.x1, h2), vx = (h2 - 0.5) * 16;
        ctx.globalAlpha = 0.85 * (1 - u);
        ctx.fillRect(Math.round(x0 + vx * u), Math.round(Math.min(f.y1, WL) - Math.sin(Math.PI * u) * (3 + h1 * 6)), 1, 1);
      } else {
        ctx.globalAlpha = 0.5 * Math.sin(Math.PI * u);
        ctx.fillRect(Math.round(150 + h1 * 180 + u * 26 + 3 * wave(t, 30 + d.id, h2)), Math.round(lerp(150, 204, h2) - u * 10), 1, 1);
      }
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= People ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const NIGHT = '#150e24', GLOWC = '#ffe6c0';
/** a look: the proportions for a height of h px, and its colours dimmed for the night (dk) */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.2, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.35), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, bag: o.bag ? m(o.bag) : null, gown: !!o.gown, cara: !!o.cara };
}
/**
 * One person, feet on row fy, centred on column x. Returns the row of the top of the head.
 * o.view: 0 from behind (facing the fountain), 1 facing us, 2 in profile (o.dir: 1 right, -1 left)
 * o.step: stride frame (profile), o.sit: seated on an edge at fy, o.kneel: on one knee, o.lean: head px towards a neighbour
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up with a phone, 3 out, 4 bent up, 5 hand to the face, 6 hand to the mouth (profile)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : o.kneel ? (P.ll >> 1) : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (P.gown) { R(tx, ly, bw, legs, P.bot); R(tx - 1, ly + 2, bw + 2, legs - 2, P.bot); R(tx - 2, fy - 1, bw + 4, 2, P.bot); }
    else if (side) {
      if (o.kneel) { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x + 1 : x - 3, fy - 1, 2, 2, P.bot); R(dir > 0 ? x - 3 : x + 1, fy, 3, 1, P.bot); }
      else if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
      if (P.cara) { R(tx, ly + 1, 1, legs - 2, '#a82a2a'); R(tx + bw - 1, ly + 1, 1, legs - 2, '#a82a2a'); }
    }
  } else if (o.sit && view === 1) {                    // seated facing us: the legs hang down in front
    R(tx, fy + 1, 2, 4, legC); R(tx + bw - 2, fy + 1, 2, 4, legC); R(tx, fy + 5, 2, 1, P.shoe); R(tx + bw - 2, fy + 5, 2, 1, P.shoe);
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                     // the fountain's light on the shoulders
  if (P.bag) { if (view === 0) R(tx + 1, ty + 1, bw - 2, th - 2, P.bag); else if (side) R(dir > 0 ? tx - 1 : tx + bw - 1, ty + 1, 2, th - 2, P.bag); }
  if (P.cara && !side) for (let k = 0; k < th - 1; k++) R(tx + Math.min(bw - 1, k), ty + k, 1, 1, '#dcd8d0');   // the white bandolier
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { for (let k = 0; k < 4; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 3 ? P.topD : P.skin); R(x + dir * 5 - (dir < 0 ? 1 : 0), ty - 5, 2, 3, '#0e1018'); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 2 || st === 4) {
        R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
        if (st === 2) { const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#62d8c8'); } }
      } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); else R(hx - 1, hy, hw + 2, 1, P.hat); }
  if (P.cara) { R(hx, hy - 2, hw, 2, '#1c2244'); R(side ? (dir > 0 ? hx : hx - 1) : hx - 1, hy, hw + (side ? 1 : 2), 1, '#0e1226'); R(hx + 1, hy - 1, 1, 1, '#e8e0c0'); }
  if (P.gown) { R(hx - 1, hy, 1, hh + 5, '#f6f2ec'); R(hx + hw, hy, 1, hh + 5, '#f6f2ec'); R(hx, hy - 1, hw, 1, '#f6f2ec'); }   // her veil
  return hy;
}
const TOPS = ['#d8404a', '#e8c040', '#ece6da', '#3a6ac8', '#2a9a8a', '#e87aa8', '#e8823a', '#4a9a4a', '#8a5ac8', '#c8d0e0', '#b8323a', '#58b0d8'];
const BOTS = ['#33466e', '#22202c', '#b09a70', '#d8d4cc', '#3a3a58', '#4a3a30'];

/* ----- the people who stay: at the rim, on the terrace, on the steps ----- */
const CARA = () => look(18, 1, 0, '#1c2244', '#161c38', { cara: true, dk: 0.1 });
const CAST = [
  { x: 96, y: 205, P: look(17, 2, 0, '#c8d0e0', BOTS[1], { hat: '#d8c8a0' }), act: 'look', ph: 0.5 },
  { x: 110, y: 205, P: look(17, 0, 2, TOPS[0], BOTS[0], { long: 2 }), act: 'film', ph: 0.1 },
  { x: 126, y: 205, P: look(17, 1, 0, TOPS[2], BOTS[1]), act: 'couple', ph: 0.3, mate: 1 },
  { x: 133, y: 205, P: look(16, 0, 3, TOPS[5], BOTS[3], { long: 3, skirt: '#e87aa8' }), act: 'couple', ph: 0.3, mate: -1 },
  { x: 150, y: 205, P: look(17, 0, 1, TOPS[1], BOTS[4], { long: 2 }), act: 'toss', ph: 7 },
  { x: 154, y: 215, P: look(19, 2, 0, TOPS[3], BOTS[1]), act: 'film', ph: 0.6, always: true },
  { x: 171, y: 205, P: look(17, 1, 0, TOPS[7], BOTS[2]), act: 'chat', dir: 1, ph: 0.2, warn: true },
  { x: 180, y: 205, P: look(17, 3, 0, TOPS[6], BOTS[0], { hat: '#d8d0c0' }), act: 'chat', dir: -1, ph: 0.7, warn: true },
  { x: 197, y: 206, P: CARA(), act: 'guardA' }, { x: 206, y: 206, P: CARA(), act: 'guardB' },
  { x: 287, y: 205, P: look(17, 1, 4, TOPS[5], BOTS[1], { long: 2 }), act: 'toss', ph: 17 },
  { x: 300, y: 205, P: look(18, 0, 1, TOPS[4], BOTS[2], { bag: '#b84a3a' }), act: 'look', ph: 0.15 },
  { x: 316, y: 205, P: look(17, 0, 3, TOPS[8], BOTS[0], { long: 2 }), act: 'selfie', ph: 0.4 }, { x: 323, y: 205, P: look(17, 2, 0, TOPS[1], BOTS[1]), act: 'selfieMate', ph: 0.4 },
  { x: 342, y: 205, P: look(17, 2, 0, TOPS[3], BOTS[3]), act: 'toss', ph: 26 },
  { x: 356, y: 205, P: look(17, 0, 5, '#7a8a9a', BOTS[4]), act: 'couple', ph: 0.8, mate: 1, old: true }, { x: 363, y: 205, P: look(16, 0, 5, '#b86a7a', BOTS[1], { skirt: '#5a4a6a' }), act: 'couple', ph: 0.8, mate: -1, old: true },
  { x: 380, y: 205, P: look(17, 1, 1, TOPS[9], BOTS[0], { long: 2 }), act: 'film', ph: 0.85 },
  { x: 398, y: 206, P: look(18, 3, 0, TOPS[10], BOTS[2], { hat: '#2a2a34' }), act: 'look', ph: 0.6 },
  { x: 84, y: 228, P: look(19, 3, 0, '#4a4a58', BOTS[1], { bag: '#7a5ac0' }), act: 'seller' },
  { x: 97, y: 230, P: look(12, 0, 3, TOPS[11], BOTS[0]), act: 'look', ph: 0.05, kid: true },
  { x: 190, y: 227, P: look(20, 0, 1, TOPS[10], BOTS[0], { dk: 0.3 }), act: 'film', ph: 0.35 },
  { x: 262, y: 245, P: look(22, 1, 0, TOPS[9], BOTS[1], { dk: 0.34 }), act: 'couple', ph: 0.55, mate: 1 }, { x: 271, y: 245, P: look(21, 0, 4, TOPS[4], BOTS[3], { long: 3, dk: 0.34 }), act: 'couple', ph: 0.55, mate: -1 },
  { x: 338, y: 227, P: look(20, 0, 2, TOPS[2], BOTS[0], { long: 3, dk: 0.3, bag: '#8a5a3a' }), act: 'look', ph: 0.9 },
  { x: 400, y: 244, P: look(22, 1, 1, TOPS[7], BOTS[4], { dk: 0.34 }), act: 'look', ph: 0.3 },
  { x: 418, y: 229, P: look(20, 0, 3, TOPS[6], BOTS[3], { long: 2, dk: 0.26 }), act: 'eat', dir: -1, ph: 0.2 },
  { x: 128, y: 246, P: look(22, 2, 0, TOPS[1], BOTS[1], { dk: 0.34 }), act: 'chat', dir: 1, ph: 0.45 }, { x: 138, y: 246, P: look(22, 0, 2, TOPS[8], BOTS[0], { long: 2, dk: 0.34 }), act: 'chat', dir: -1, ph: 0.1 },
  ...[[52, 230, 0, 1], [60, 230, 4, -1], [300, 230, 3, 1], [308, 230, 5, 0], [316, 230, 1, -1], [426, 230, 6, 1], [434, 230, 2, -1], [176, 248, 9, 1], [185, 248, 5, -1],
    [236, 248, 7, 0], [364, 248, 0, 1], [373, 248, 11, -1], [28, 248, 8, 0]].map(([x, y, c, lean], k) =>
    ({ x, y, P: look(y > 240 ? 22 : 20, k % 4, (k * 3) % 6, TOPS[c], BOTS[k % 6], { long: k % 3 === 1 ? 3 : 0, dk: y > 240 ? 0.36 : 0.3 }), act: 'sit', lean, ph: (k * 0.37) % 1 })),
  { x: 449, y: 247, P: look(22, 1, 0, TOPS[0], BOTS[1], { hat: '#2a2a34', dk: 0.3 }), act: 'bubbler' },
  { x: 40, y: 268, P: look(26, 1, 0, TOPS[3], BOTS[1], { hat: '#c8b88a', dk: 0.42 }), act: 'look', ph: 0.7 },
  { x: 200, y: 269, P: look(26, 0, 1, TOPS[0], BOTS[0], { dk: 0.42 }), act: 'film', ph: 0.0, always: true },
  { x: 322, y: 269, P: look(26, 2, 0, TOPS[2], BOTS[4], { dk: 0.42 }), act: 'couple', ph: 0.15, mate: 1 }, { x: 333, y: 269, P: look(25, 0, 2, TOPS[5], BOTS[1], { long: 4, dk: 0.42 }), act: 'couple', ph: 0.15, mate: -1 },
  { x: 388, y: 268, P: look(26, 3, 0, TOPS[4], BOTS[2], { dk: 0.42, bag: '#3a4a6a' }), act: 'look', ph: 0.25 },
];

/* ================= The loop's timeline (seconds) ================= */
const TOUR = { in: 30, turn: 58, toss: 62, cheer: [63.4, 66.6], out: 75 };          // a tour group lines up and throws all at once
const PROP = { in: 109, stand: 126, kneel: 130.8, yes: 136, hug: 137.6, out: 150 }; // a proposal in the middle of the rim
const WED = { in: 175, pose: 188.5, kiss: [198, 203], out: 206 };                   // a wedding couple poses for their photographer
const WED_FLASH = [190.4, 192.1, 194.2, 196.6, 199.2, 200.4, 201.8, 204.3];
const WARN1 = { in: 88, sit: 100.8, whistle: 103, up: 105.5, x: 270 };              // someone sits on the rim: a carabiniere whistles
const WARN2 = { sit: 221, whistle: 225, up: 227 };                                  // and again, the two friends on the left
const KID_RUNS = [16, 154];                                                         // a child runs to the rim, the pigeons scatter
const BELL_T = 112;                                                                 // the church strikes; the pigeons on the cornice start up
const CHIME_T = 198.4;                                                              // and its small bells ring for the newly-weds' kiss
const TOSS_EVERY = 30;                                                              // each coin tosser at the rim throws this often
const CHEERS = [[136.3, 140.6, 242], [198.6, 202.6, 252], [63.4, 66.2, 244]];       // arms go up around [from, to, column]
const cheering = (x, t) => CHEERS.some(([a, b, cx]) => t >= a && t < b && Math.abs(x - cx) < 100);
const walkF = t => Math.floor(t * 3.2) & 1;            // stride frame (3.2 a second: a whole number per loop)

let DL = [];                                           // the frame's people, drawn back to front
const put = (y, f) => DL.push({ y, f });
const cone = (x, y, id) => { R(x, y, 1, 1, ['#ff8ab0', '#f6eccc', '#8cc060'][id % 3]); R(x, y + 1, 1, 2, '#d8a860'); };
const rose = (x, y) => { R(x, y, 1, 1, '#e02a3a'); R(x, y + 1, 1, 2, '#2a5a2a'); };
function whistle(x, y, s, dir) {                       // a whistle's shrill, as three little ticks
  if (s < 0 || s > 1.3 || !(Math.floor(s * 10) & 1)) return;
  R(x + dir * 2, y - 2, 1, 1, '#ffffff'); R(x + dir * 3, y, 1, 1, '#ffffff'); R(x + dir * 2, y + 2, 1, 1, '#ffffff');
}
function castPose(c, i, t) {
  const o = { view: 0 };
  switch (c.act) {
    case 'film': if (c.always || frac(t / 24 + c.ph) < 0.62) o.aR = 2; break;
    case 'look': { const u = frac(t / 20 + c.ph); if (u < 0.1) o.aR = 1; else if (u > 0.5 && u < 0.58) { o.view = 2; o.dir = (i & 1) ? 1 : -1; } break; }
    case 'couple': {
      const u = frac(t / 40 + c.ph);
      if (!c.old && u > 0.5 && u < 0.6) { o.view = 2; o.dir = c.mate; o.lean = c.mate; }   // a kiss
      else if (c.mate > 0) o.aR = u < 0.3 ? 3 : 0;                                          // an arm round her
      else if (u > 0.7 && u < 0.78) o.aL = 1;                                               // she points something out
      break;
    }
    case 'toss': {
      const d = (((t - c.ph) % TOSS_EVERY) + TOSS_EVERY) % TOSS_EVERY;
      o.view = d > 1.4 && d < 3.6 ? 0 : 1;                                                  // back to the fountain; then a look at the splash
      if (d > TOSS_EVERY - 0.9 || d < 0.35) o.aR = 4;
      break;
    }
    case 'chat': o.view = 2; o.dir = c.dir; if (hash(i, step(t + c.ph * 3, 80), 31) < 0.35) o.aR = 3; break;
    case 'selfie': o.view = 1; if (frac(t / 16 + c.ph) < 0.7) o.aL = 2; break;
    case 'selfieMate': o.view = 1; o.lean = -1; if (frac(t / 16 + c.ph) > 0.75) o.aR = 1; break;
    case 'eat': o.view = 2; o.dir = c.dir; if (frac(t / 6 + c.ph) < 0.35) o.aR = 6; break;
    case 'sit': o.sit = true; if (frac(t / 30 + c.ph) < 0.4) o.lean = c.lean; if (frac(t / 48 + c.ph) < 0.12) o.aR = 2; break;
    case 'seller': o.view = 2; o.dir = 1; if (frac((t - 2) / 6) < 0.1) o.aR = 1; break;
    case 'bubbler': o.view = 2; o.dir = -1; o.aR = frac((t - BUBBLE_T) / BUBBLE_EVERY) < 0.45 ? 1 : 3; break;
    default: { o.view = 1; const h = hash(i, step(t, 30), 32); if (h < 0.25) { o.view = 2; o.dir = h < 0.12 ? 1 : -1; } }   // the carabinieri look about
  }
  if (CHEER_ACTS.includes(c.act) && hash(i, 9, 33) < 0.7 && cheering(c.x, t)) { o.view = 0; o.lean = 0; o.aL = 1; o.aR = 1; }
  return o;
}
const CHEER_ACTS = ['film', 'look', 'couple', 'sit'];
function drawCast(t) {
  CAST.forEach((c, i) => {
    const x = c.x, y = c.y, o = castPose(c, i, t), P = c.P;
    if (c.warn && t >= WARN2.sit && t < WARN2.up + 1.6) {                      // the two friends sit down on the rim
      if (t < WARN2.up) { put(y, () => person(x, RIM + 1, P, { view: 1, sit: true })); return; }
      o.view = 1; o.aL = 5; o.aR = 5;
    }
    if (c.act === 'guardA' && t >= WARN2.whistle && t < WARN2.up + 0.6) {
      put(y, () => { const hy = person(x, y, P, { view: 2, dir: -1, aR: 1 }); whistle(x, hy + 2, t - WARN2.whistle, -1); });
      return;
    }
    if (c.act === 'guardB' && t >= WARN1.whistle && t < 112.2) {               // he walks over to the one on the rim, and back
      const s = t - WARN1.whistle;
      if (s < 0.9) put(y, () => { const hy = person(x, y, P, { view: 2, dir: 1, aR: 1 }); whistle(x, hy + 2, s, 1); });
      else if (s < 4.2) { const X = lerp(x, 252, (s - 0.9) / 3.3); put(y + 1, () => person(X, y + 1, P, { view: 2, dir: 1, step: walkF(t), aR: 3 })); }
      else if (s < 5.6) put(y + 1, () => person(252, y + 1, P, { view: 2, dir: 1 }));
      else { const X = lerp(252, x, (s - 5.6) / 3.6); put(y + 1, () => person(X, y + 1, P, { view: 2, dir: -1, step: walkF(t) })); }
      return;
    }
    put(y, () => {
      const hy = person(x, y, P, o);
      if (c.act === 'eat') cone(x + c.dir * 2, hy + P.hh + (o.aR === 6 ? -1 : 2), 0);
      if (c.act === 'bubbler') { const up = o.aR === 1; R(x - 2 - (up ? 0 : 4), hy + (up ? -8 : P.hh + 1), 1, 5, '#c8b890'); R(x - 3 - (up ? 0 : 4), hy + (up ? -9 : P.hh), 3, 1, '#e8f6ff'); }   // his wand
    });
  });
}

/* ----- people walking through: along the terrace and across the steps ----- */
const WALKERS = [];
{
  const r = rng(94);
  for (const [y, h, n] of [[209, 18, 4], [221, 20, 3], [239, 22, 3], [257, 24, 2]]) for (let k = 0; k < n; k++) {
    const dir = (k + (y & 2)) & 1 ? 1 : -1, sp = 4 + Math.floor(r() * 4), ph = (k + r() * 0.6) / n;
    const mk = () => look(h - (r() < 0.3 ? 1 : 0), Math.floor(r() * 4), Math.floor(r() * 6), TOPS[Math.floor(r() * 12)], BOTS[Math.floor(r() * 6)],
      { long: r() < 0.4 ? 2 : 0, skirt: r() < 0.2 ? TOPS[Math.floor(r() * 12)] : null, bag: r() < 0.3 ? '#6a4a3a' : null, hat: r() < 0.15 ? '#e8dcc0' : null, dk: 0.2 + (y - 209) * 0.004 });
    WALKERS.push({ y, n: sp, ph, dir, P: mk(), cone: r() < 0.3 });
    if (r() < 0.45) WALKERS.push({ y, n: sp, ph, dir, P: mk(), off: 8 });       // two walking together
  }
}
function drawWalkers(t) {
  WALKERS.forEach((w, i) => {
    const u = frac(t * w.n / LOOP + w.ph), x = (w.dir > 0 ? -16 + u * (W + 32) : W + 16 - u * (W + 32)) + (w.off || 0);
    if (x < -8 || x > W + 8) return;
    put(w.y, () => { const hy = person(x, w.y, w.P, { view: 2, dir: w.dir, step: (walkF(t) + i) & 1, aR: w.cone ? 3 : 0 }); if (w.cone) cone(Math.round(x) + w.dir * 4, hy + w.P.hh, i); });
  });
}

/* ----- people coming in from the streets beside the palace, and leaving by them: they grow as they come ----- */
const SIDERS = [];
{
  const r = rng(96);
  for (let k = 0; k < 8; k++) {
    const top = TOPS[Math.floor(r() * 12)], bot = BOTS[Math.floor(r() * 6)], sk = Math.floor(r() * 4), hr = Math.floor(r() * 6), long = r() < 0.4 ? 2 : 0, L = [];
    for (let h = 9; h <= 17; h++) L[h] = look(h, sk, hr, top, bot, { long: h > 12 ? long : 0, dk: 0.16 });
    SIDERS.push({ east: k % 2 === 0, L, n: 6 + Math.floor(r() * 4), ph: (k + r() * 0.5) / 8, away: r() < 0.45, off: Math.floor(r() * 9) - 4 });
  }
}
function drawSiders(t) {
  SIDERS.forEach((w, i) => {
    const u0 = frac(t * w.n / LOOP + w.ph), u = w.away ? 1 - u0 : u0, e = Math.pow(u, 1.7);
    const x = lerp(110 + w.off * 0.4, -10, e), y = lerp(154, 204, e) + w.off * e, X = w.east ? 479 - x : x, h = Math.round(lerp(9, 17, e));   // from the street's mouth, clear of the basin's side rim
    const dir = (w.east ? 1 : -1) * (w.away ? -1 : 1);
    put(y, () => {
      ctx.globalAlpha = Math.min(1, u / 0.07);         // out of the street's shadow
      person(X, y, w.L[h], { view: 2, dir, step: (walkF(t) + i) & 1 });
      ctx.globalAlpha = 1;
    });
  });
}

/* ----- the gelato queue on the west side: every 12 s one is served and walks off with a cone ----- */
const QLOOK = [];                                      // [person][height 13..18]: they grow as they walk towards us
for (let i = 0; i < 5; i++) { QLOOK[i] = []; for (let h = 13; h <= 18; h++) QLOOK[i][h] = look(h, i % 4, (i * 2 + 1) % 6, TOPS[(i * 4 + 2) % 12], BOTS[(i + 2) % 6], { long: i % 2 ? 2 : 0, dk: 0.14 }); }
const QSLOT = [[9, 170], [17, 171], [25, 172], [33, 173], [-8, 180]];
function drawQueue(t) {
  const c = Math.floor(t / 12), u = frac(t / 12), mv = clamp((u - 0.82) / 0.18, 0, 1), st = walkF(t);
  for (let j = 0; j < 5; j++) {
    if ((j === 4 && !mv) || (j === 0 && mv)) continue;
    const [x0, y0] = QSLOT[j], [x1, y1] = QSLOT[Math.max(0, j - 1)], x = lerp(x0, x1, mv), y = lerp(y0, y1, mv), P = QLOOK[(c + j) % 5][13];
    put(y, () => person(x, y, P, { view: 2, dir: j === 4 ? 1 : -1, step: mv > 0 && mv < 1 ? (st + j) & 1 : 0, aR: j === 0 && u > 0.55 ? 3 : 0 }));
  }
  for (let m = 0; m < 4; m++) {
    const id = (((c - m) % 20) + 20) % 20, s = (((t - (id * 12 + 9.84)) % LOOP) + LOOP) % LOOP;
    if (s > 35.5) continue;
    // down the walkway past the rim's west end (it starts at x = 36), then along the terrace
    const x = s < 4 ? lerp(9, 34, s / 4) : 34 + (s - 4) * 15, y = s < 4 ? lerp(170, 209, s / 4) : 209, P = QLOOK[id % 5][Math.round(lerp(13, 18, Math.min(1, s / 4)))];
    if (x > 492) continue;
    put(y, () => { const hy = person(x, y, P, { view: 2, dir: 1, step: (st + m) & 1, aR: 3 }); cone(Math.round(x) + 4, hy + P.hh, id); });
  }
}

/* ----- the tour group: in behind the guide's flag, a line along the rim, one big throw, out again ----- */
const TOURISTS = Array.from({ length: 8 }, (_, i) => look(17, i % 4, (i * 5) % 6, TOPS[(i * 5 + 1) % 12], BOTS[i % 6],
  { hat: i % 3 === 0 ? ['#f4f0e8', '#c83a30', '#3a6ac8'][(i / 3) | 0] : null, bag: i % 2 ? '#5a6a8a' : null, long: i % 4 === 2 ? 2 : 0 }));
const GUIDE = look(17, 1, 4, '#f4f0e8', BOTS[4], { long: 2 });
const slotX = i => 216 + 8 * i, tossT = i => TOUR.toss + i * 0.12;
function drawTour(t) {
  if (t < TOUR.in || t > 104) return;
  const st = walkF(t);
  let gx = 207, gy = 207, go = { view: 2, dir: 1 };
  if (t < 44.8) { gx = lerp(-12, 207, (t - TOUR.in) / 14.8); gy = 209; go.step = st; }
  else if (t >= 72.5) { gx = lerp(207, 492, (t - 72.5) / 19); gy = 210; go.step = st; }
  if (gx < 492) put(gy, () => {
    const hy = person(gx, gy, GUIDE, go), fx = Math.round(gx) + 2, up = t > TOUR.turn && t < 63 ? 3 + (Math.floor(t * 4) & 1) : 0;
    R(fx, hy - 7 - up, 1, 9 + up, '#c8c8d4'); R(fx + 1, hy - 7 - up, 3, 2, '#ff4a4a'); R(fx + 1, hy - 5 - up, 2, 1, '#c82a2a');
  });
  for (let i = 0; i < 8; i++) {
    const sx = slotX(i), s0 = 32 + (7 - i) * 1.1, a = s0 + (sx + 12) / 15, o0 = TOUR.out + (7 - i) * 1.2, P = TOURISTS[i];
    if (t < s0) continue;
    if (t < a) { const x = lerp(-12, sx, (t - s0) / (a - s0)); put(209, () => person(x, 209, P, { view: 2, dir: 1, step: (st + i) & 1 })); continue; }
    if (t >= o0) { const x = sx + (t - o0) * 15; if (x < 494) put(209, () => person(x, 209, P, { view: 2, dir: 1, step: (st + i) & 1 })); continue; }
    const o = { view: 0 }, T = tossT(i);
    let y = 205;
    if (t < TOUR.turn + i * 0.15) { if (i % 3 === 0) o.aR = 2; }
    else if (t < TOUR.cheer[1]) {
      o.view = 1;
      if (t > T - 0.9 && t < T + 0.4) o.aR = 4;
      else if (t >= TOUR.cheer[0]) { o.aL = 1; o.aR = 1; y -= (Math.floor(t * 4 + i) & 1); }
    } else if (i & 1) o.aR = 2;
    put(205, () => person(sx, y, P, o));
  }
}

/* ----- coins: an arc over the shoulder, a glint at the top, a splash and a ring ----- */
const COINS = [];
{
  const r = rng(93);
  for (const c of CAST) if (c.act === 'toss') for (let k = 0; k < LOOP / TOSS_EVERY; k++)
    COINS.push({ t: c.ph + k * TOSS_EVERY, x: c.x + 3, y: c.y - c.P.h + 1, dx: (r() - 0.5) * 12, y1: 174 + r() * 10, hgt: 16 + r() * 8 });
  for (let i = 0; i < 8; i++) COINS.push({ t: tossT(i), x: slotX(i) + 3, y: 189, dx: (r() - 0.5) * 12, y1: 171 + r() * 12, hgt: 16 + r() * 9, wave: true });
  COINS.push({ t: TOUR.toss + 0.5, x: 210, y: 190, dx: 5, y1: 178, hgt: 18, wave: true });   // the guide's own
}
const COIN_AIR = 1.1;
function drawCoins(t, flying) {
  for (const c of COINS) {
    const s = t - c.t;
    if (s < 0 || s > 3.2 || (s < COIN_AIR) !== flying) continue;
    if (flying) {
      const u = s / COIN_AIR, x = Math.round(c.x + c.dx * u), y = Math.round(lerp(c.y, c.y1, u) - 4 * c.hgt * u * (1 - u));
      const u0 = Math.max(0, u - 0.07);
      R(Math.round(c.x + c.dx * u0), Math.round(lerp(c.y, c.y1, u0) - 4 * c.hgt * u0 * (1 - u0)), 1, 1, '#a87820');
      R(x, y, 1, 1, (Math.floor(s * 20) & 1) ? '#fffbe0' : '#f0c040');
      halo(HL.glint, x, y, u > 0.3 && u < 0.6 ? 1 : 0.45);
      continue;
    }
    const v = s - COIN_AIR, x = Math.round(c.x + c.dx), y = Math.round(c.y1);
    if (v < 0.6) { const hgt = Math.sin(Math.PI * v / 0.6) * 6; for (const k of [-2, -1, 0, 1, 2]) R(x + Math.round(k * (0.5 + v * 2)), Math.round(y - hgt * (1 - Math.abs(k) * 0.3)), 1, k ? 1 : 2, '#ffffff'); }
    const u2 = v / 2.1, rx = Math.round(2 + u2 * 9), ry = 1 + u2 * 2.6;
    ctx.globalAlpha = (1 - u2) * 0.85; ctx.fillStyle = '#e4fff8';
    for (let dx = -rx; dx <= rx; dx++) { const dy = Math.round(ry * Math.sqrt(1 - (dx / rx) ** 2)); ctx.fillRect(x + dx, y - dy, 1, 1); if (dy) ctx.fillRect(x + dx, y + dy, 1, 1); }
    ctx.globalAlpha = 1;
  }
}

/* ----- the proposal: he kneels, she nods, the piazza cheers ----- */
const HE = look(18, 1, 0, '#2c3c70', BOTS[1], { dk: 0.15 }), SHE = look(17, 0, 3, '#ece4d8', '#c83a4a', { long: 3, skirt: '#c83a4a', dk: 0.12 });
function drawProposal(t) {
  if (t < PROP.in || t >= 171) return;
  const st = walkF(t);
  if (t < PROP.stand || t >= PROP.out) {
    const out = t >= PROP.out, x = out ? lerp(238, 492, (t - PROP.out) / 21) : lerp(-14, 236, (t - PROP.in) / (PROP.stand - PROP.in));
    put(209, () => {
      person(x, 209, HE, { view: 2, dir: 1, step: st });
      const hy = person(x + 8, 209, SHE, { view: 2, dir: 1, step: 1 - st, aR: out ? 3 : 0 });
      if (out) rose(Math.round(x) + 13, hy + SHE.hh);
    });
    return;
  }
  put(205.5, () => {
    if (t < 129.5) { person(238, 205, HE); person(247, 205, SHE, { aL: t > 127.6 ? 3 : 0 }); }
    else if (t < PROP.kneel) { person(238, 206, HE, { view: 2, dir: 1 }); person(247, 205, SHE, { view: t > 130.2 ? 2 : 0, dir: -1 }); }
    else if (t < PROP.yes + 0.9) {
      const hy = person(238, 207, HE, { view: 2, dir: 1, kneel: true, aR: 3 }), g = Math.floor(t * 12) & 1;
      person(247, 205, SHE, { view: 2, dir: -1, aR: t > 131.6 ? 6 : 0 });
      R(242, hy + HE.hh + 1, 1, 1, g ? '#ffffff' : '#ffe070'); halo(HL.glint, 242, hy + HE.hh + 1, 0.6 + 0.4 * g);   // the ring
    } else if (t < PROP.hug) { person(238, 205, HE, { view: 2, dir: 1, aR: 3 }); person(247, 205, SHE, { view: 2, dir: -1, aR: 3 }); }
    else {
      person(240, 205, HE, { view: 2, dir: 1, aR: 3 });
      const hy = person(245, 205, SHE, { view: 2, dir: -1, aR: 3, lean: -1 });
      for (let k = 0; k < 3; k++) {                    // a few hearts
        const s = (t - 138.5 - k * 1.3) / 2.6;
        if (s <= 0 || s >= 1) continue;
        const x = 242 + k * 2 + Math.round(Math.sin(s * 6 + k) * 1.5), y = hy - 2 - Math.round(s * 9);
        ctx.globalAlpha = 1 - s; R(x - 1, y, 3, 1, '#ff6a9a'); R(x, y + 1, 1, 1, '#ff6a9a'); ctx.globalAlpha = 1;
      }
      if (t > 148.6) rose(249, hy + SHE.hh);
    }
  });
}

/* ----- the wedding couple and their photographer ----- */
const BRIDE = look(17, 0, 1, '#f6f2ec', '#f6f2ec', { gown: true, dk: 0.04 }), GROOM = look(18, 1, 0, '#22222e', '#1c1c26', { dk: 0.05 });
const PHOTOG = look(18, 2, 0, '#4a4a56', BOTS[1], { bag: '#6a5238', dk: 0.25 });
function drawWedding(t) {
  if (t < WED.in - 1 || t >= 222) return;
  const st = walkF(t);
  const px = t < 186.6 ? lerp(492, 254, (t - 174) / 12.6) : t < 206.6 ? 254 : lerp(254, -14, (t - 206.6) / 15);
  put(219, () => person(px, 219, PHOTOG, px === 254 ? { view: 0, kneel: true, aR: 2 } : { view: 2, dir: -1, step: st }));
  if (t < WED.in) return;
  if (t < WED.pose || t >= WED.out) {
    const x = t < WED.pose ? lerp(492, 248, (t - WED.in) / (WED.pose - WED.in)) : lerp(248, -16, (t - WED.out) / 15.5);
    put(209, () => { person(x, 209, BRIDE, { view: 2, dir: -1, step: st }); person(x + 9, 209, GROOM, { view: 2, dir: -1, step: 1 - st }); });
    return;
  }
  put(205.4, () => {
    const kiss = t >= WED.kiss[0] && t < WED.kiss[1];
    if (t >= 195.5 && t < WED.kiss[1] + 0.6) {
      person(kiss ? 250 : 248, 205, BRIDE, { view: 2, dir: 1, lean: kiss ? 1 : 0 });
      person(kiss ? 255 : 257, 205, GROOM, { view: 2, dir: -1, aR: kiss ? 3 : 0, lean: kiss ? -1 : 0 });
    } else {
      const wv = t > 203.6 && (Math.floor(t * 4) & 1), hy = person(248, 205, BRIDE, { view: 1, aL: wv ? 1 : 0 });
      person(257, 205, GROOM, { view: 1, aR: t > 203.6 ? 1 : 0 });
      R(248, hy + BRIDE.hh + 3, 2, 2, '#ff8ab0'); R(249, hy + BRIDE.hh + 2, 1, 1, '#ffe0ec');   // her bouquet
    }
  });
}

/* ----- the one who sits on the rim, the child and the pigeons ----- */
const SITTER = look(17, 0, 2, TOPS[8], BOTS[0]), KID = look(12, 0, 3, '#ff7a3a', '#3a6ac8', { dk: 0.25 });
function drawSitter(t) {
  if (t < WARN1.in || t >= 121.4) return;
  const st = walkF(t), X = WARN1.x;
  if (t < WARN1.sit) { const x = lerp(492, X, (t - WARN1.in) / (WARN1.sit - WARN1.in)); put(209, () => person(x, 209, SITTER, { view: 2, dir: -1, step: st })); }
  else if (t < WARN1.up) put(205, () => person(X, RIM + 1, SITTER, { view: 1, sit: true, aR: t > 102 ? 2 : 0 }));
  else if (t < WARN1.up + 1.1) put(205, () => person(X, 205, SITTER, { view: 1, aL: 5, aR: 5 }));
  else { const x = X + (t - WARN1.up - 1.1) * 15; put(209, () => person(x, 209, SITTER, { view: 2, dir: 1, step: st })); }
}
function drawKid(t) {
  let x = 409, y = 244, o = { view: 0 };
  for (const t0 of KID_RUNS) {
    const s = t - t0;
    if (s < 0 || s >= 12.5) continue;
    const run = { view: 2, step: Math.floor(t * 6.4) & 1 };
    if (s < 3.2) { x = lerp(409, 352, s / 3.2); y = lerp(244, 208, s / 3.2); o = { ...run, dir: -1 }; }
    else if (s < 9) { x = 352; y = 208 - (Math.floor(t * 4) & 1 && frac(s / 2) < 0.5 ? 1 : 0); o = { view: 0, aL: frac(s / 2) < 0.5 ? 1 : 0, aR: frac(s / 2) < 0.5 ? 1 : 0 }; }
    else { x = lerp(352, 409, (s - 9) / 3.5); y = lerp(208, 244, (s - 9) / 3.5); o = { ...run, dir: 1 }; }
  }
  put(y, () => person(x, y, KID, o));
}
const PIGEONS = [[338, 211], [346, 214], [353, 210], [361, 213], [368, 210], [374, 214], [330, 214]];
function birdAir(x, y, fl, col) { x = Math.round(x); y = Math.round(y); R(x, y, 1, 1, col); R(x - 1, y - (fl ? 1 : 0), 1, 1, col); R(x + 1, y - (fl ? 1 : 0), 1, 1, col); if (!fl) { R(x - 2, y, 1, 1, col); R(x + 2, y, 1, 1, col); } }
function drawPigeons(t) {
  PIGEONS.forEach(([hx, hy], i) => {
    let s = -1;
    for (const t0 of KID_RUNS) { const q = (t - t0 - 1.5 - i * 0.07) / (6.5 + (i % 3) * 0.7); if (q >= 0 && q < 1) s = q; }
    if (s < 0) {                                       // pecking about
      const h = hash(i, step(t + i * 0.13, 480), 41), wx = hx + Math.round(2 * wave(t, 3 + i, i * 0.2)), d = i & 1 ? 1 : -1;
      put(hy, () => { R(wx - 1, hy - 1, 3, 2, '#7c8094'); R(wx - d, hy - 1, 1, 1, '#a8acc0'); R(wx + d * 2, hy - (h < 0.3 ? 0 : 2), 1, 1, '#5c6c80'); R(wx, hy + 1, 1, 1, '#b05a4a'); });
    } else {                                           // up and round over the basin
      const e = Math.sin(Math.PI * s), x = hx - (40 + i * 9) * e * (0.6 + 0.4 * Math.cos(Math.PI * s)) + 6 * Math.sin(TAU * s * 2 + i);
      put(400, () => birdAir(x, hy - (34 + (i % 4) * 6) * Math.pow(e, 0.7), Math.floor(t * 8 + i) & 1, '#c6cadc'));
    }
  });
}
const ROOSTS = [131, 149, 156, 175, 344, 351];         // pigeons roosting on the wings' cornices
function drawRoosts(t) {
  ROOSTS.forEach((x, i) => {
    let s = -1;
    for (const t0 of [BELL_T, CHIME_T]) { const q = (t - t0 - 0.3 - i * 0.12) / (5 + (i % 3)); if (q >= 0 && q < 1) s = q; }
    if (s >= 0) { const e = Math.sin(Math.PI * s); birdAir(x + (i & 1 ? 1 : -1) * 14 * e * Math.sin(TAU * s), 57 - 16 * e, Math.floor(t * 8 + i) & 1, '#b0b4c8'); }
    else { R(x, 57, 2, 2, '#6a6e84'); R(x + (i & 1 ? 2 : -1), 56 + (hash(i, step(t, 60), 43) < 0.2 ? 1 : 0), 1, 1, '#8c90a6'); }
  });
}
const GULL_PASS = [{ a: 38, b: 54, x0: -10, x1: 490, y0: 17, y1: 8 }, { a: 164, b: 180, x0: 490, x1: -10, y0: 9, y1: 18 }];
const GULL_F = [['#...#', '.#.#.', '..#..'], ['##.##', '..#..'], ['..#..', '.#.#.', '#...#']];
function drawGulls(t) {
  for (const g of GULL_PASS) for (let k = 0; k < 2; k++) {
    const s = (t - g.a - k * 1.7) / (g.b - g.a);
    if (s < 0 || s >= 1) continue;
    const x = Math.round(lerp(g.x0, g.x1, s)), y = Math.round(lerp(g.y0, g.y1, s) + k * 6 + 2 * wave(t, 30 + k * 7, k * 0.3));
    const fl = frac(t * 1.6 + k * 0.37), fr = fl < 0.55 ? 1 : fl < 0.7 ? 0 : fl < 0.85 ? 2 : 1;
    ctx.fillStyle = '#f0dcc0';
    GULL_F[fr].forEach((row, j) => { for (let q = 0; q < 5; q++) if (row[q] === '#') ctx.fillRect(x - 2 + q, y - 1 + j, 1, 1); });
  }
}

/* ----- the rose seller works the crowd; the toy man's light-up spinners shoot up and float down ----- */
const ROSE = look(18, 2, 0, '#6a7a52', BOTS[1]);
const ROSE_KEYS = [
  [[4, 492], [13.3, 352], [19, 352], [22.5, 300], [28, 300], [36.7, 170], [42, 170], [44.8, 128], [50, 128], [51, 116], [70, 116], [78.7, -14]],
  [[130, -14], [146, 226], [150.5, 226], [155.4, 300], [160, 300], [172.8, 492]],
];
function drawRose(t) {
  for (const keys of ROSE_KEYS) {
    if (t < keys[0][0] || t >= keys[keys.length - 1][0]) continue;
    let k = 0;
    while (t >= keys[k + 1][0]) k++;
    const [ta, xa] = keys[k], [tb, xb] = keys[k + 1], x = lerp(xa, xb, (t - ta) / (tb - ta)), moving = xa !== xb;
    const dir = moving ? Math.sign(xb - xa) : (k > 0 ? Math.sign(xa - keys[k - 1][1]) || 1 : 1), offer = !moving && frac(t / 3) < 0.6;
    put(211, () => {
      const hy = person(x, 211, ROSE, { view: 2, dir, step: moving ? walkF(t) : 0, aR: offer ? 3 : 0 }), X = Math.round(x), by = hy + ROSE.hh + 1;
      R(X - dir * 2, by + 1, 1, 4, '#2a5a2a'); R(X - dir * 2 - 1, by - 1, 3, 2, '#e02a3a'); R(X - dir * 2, by - 2, 1, 1, '#ff5a6a');   // the bunch on his arm
      if (offer) rose(X + dir * 5, by);
    });
  }
}
const TOY_EVERY = 6, TOY_X = 89;
function drawToys(t) {
  const k0 = Math.floor((t - 2) / TOY_EVERY), n = LOOP / TOY_EVERY;
  for (let k = k0 - 1; k <= k0; k++) {
    const kk = ((k % n) + n) % n, s = (((t - (2 + kk * TOY_EVERY)) % LOOP) + LOOP) % LOOP;
    if (s > 7.2) continue;
    const h0 = hash(kk, 1, 71), h1 = hash(kk, 2, 71), top = 150 + h0 * 18;
    let x, y;
    if (s < 0.7) { const u = s / 0.7; x = TOY_X + u * 8 * h1; y = lerp(208, top, 1 - (1 - u) * (1 - u)); }
    else { const v = (s - 0.7) / 6.5; x = TOY_X + 8 * h1 + v * (16 + 60 * h1) + 6 * Math.sin(v * 9 + kk); y = lerp(top, 214 + h0 * 14, Math.pow(v, 1.3)); }
    const bl = Math.floor(t * 10) & 1, X = Math.round(x), Y = Math.round(y);
    R(X - 1, Y, 3, 1, bl ? '#5ac8ff' : '#ff6ae0'); R(X, Y, 1, 1, '#ffffff');
    if (bl) R(X, Y + 1, 1, 1, '#9aa0b8');
    halo(HL.toy, X, Y, 0.9);
  }
}

/* ----- a man with a bubble wand on the east steps: every 12 s a run of big soap bubbles drifts over the crowd ----- */
const BUBBLE_EVERY = 12, BUBBLE_T = 5, BUBBLER = { x: 449, y: 247 };
const BUBBLES = Array.from({ length: 9 }, (_, i) => ({ id: i, lag: i * 0.045, r: 2 + (i % 3) }));       // let go one after another
function drawBubbles(t) {
  for (const b of BUBBLES) {
    const { cyc, u } = cycle(t, LOOP / BUBBLE_EVERY, 1 - BUBBLE_T / BUBBLE_EVERY - b.lag);
    if (u > 0.55) continue;                            // each lives 6.6 s
    const v = u / 0.55, h1 = hash(b.id, cyc, 81), h2 = hash(b.id, cyc, 82);
    const x = Math.round(BUBBLER.x - 7 - v * (30 + 80 * h1) + 4 * Math.sin(v * 7 + b.id)), y = Math.round(BUBBLER.y - 27 - v * (16 + 40 * h2) - 5 * Math.sin(v * 3 + b.id));
    if (v > 0.94) { for (const [dx, dy] of [[-2, -1], [2, -2], [-1, 2], [3, 1]]) R(x + dx, y + dy, 1, 1, '#e8f6ff'); continue; }   // it pops
    const r = Math.max(1, Math.round(b.r * Math.min(1, v * 6)));
    ctx.globalAlpha = 0.6;
    for (let dx = -r; dx <= r; dx++) { const dy = Math.round(Math.sqrt(r * r - dx * dx)); R(x + dx, y - dy, 1, 1, dx < 0 ? '#c8ecff' : '#f0c8ff'); if (dy) R(x + dx, y + dy, 1, 1, dx < 0 ? '#a8f0e0' : '#c8d0ff'); }
    ctx.globalAlpha = 1; R(x - (r > 1 ? 1 : 0), y - (r > 1 ? 1 : 0), 1, 1, '#ffffff');
  }
}

/* ----- camera flashes: a few all the time, a burst at each big moment ----- */
const FLASHES = [];
{
  const r = rng(95), stand = CAST.filter(c => c.act !== 'sit');
  const clear = t => { const f = STORM_FLASHES.find(f => t > f.t - 0.3 && t < f.t + 2.3); return f ? f.t + 2.4 : t; };   // never on top of a picked storm's own flash
  const at = (c, t, big) => FLASHES.push({ t: clear(t), x: c.x + 3, y: c.y - c.P.h - 1, big });
  for (const c of CAST) if (c.act === 'film' || c.act === 'selfie') for (let k = 0; k < 6; k++) at(c, 3 + r() * 232);
  const burst = (a, b, n, cx) => { const near = stand.filter(c => Math.abs(c.x - cx) < 130); for (let k = 0; k < n; k++) at(near[Math.floor(r() * near.length)], a + r() * (b - a)); };
  burst(61.6, 67, 14, 244); burst(135.6, 143, 18, 242); burst(189, 204, 12, 252);
  burst(6, 9.5, 7, 330); burst(232.5, 236, 7, 150);                                            // two group photos
  for (const t of WED_FLASH) FLASHES.push({ t, x: 255, y: 207, big: true });
}
function drawFlashes(t, reducedMotion) {
  for (const f of FLASHES) {
    const s = t - f.t;
    if (s < 0 || s >= 0.16) continue;
    R(f.x, f.y, 2, 2, '#ffffff');
    if (reducedMotion) continue;                       // the glint stays, the burst of light doesn't
    halo(HL.flash, f.x, f.y, (1 - s / 0.16) * (f.big ? 1 : 0.7));
    if (f.big) halo(HL.flash, f.x, f.y, 0.6 * (1 - s / 0.16));
  }
}
function drawCrowd(t) {
  DL = [];
  drawCast(t); drawWalkers(t); drawSiders(t); drawQueue(t); drawTour(t); drawProposal(t); drawWedding(t); drawSitter(t); drawKid(t); drawPigeons(t); drawRose(t);
  put(CHURCH_FOOT - 0.5, () => blit(CHURCH_C));      // the church's corner covers everyone whose feet are further away than its own
  DL.sort((a, b) => a.y - b.y);
  for (const d of DL) d.f();
}

/* ================= A tricolour on the east side, the shops' light ================= */
const FLAG = { x: 392, y: 92 };
function drawFlag(t) {
  for (let k = 0; k < 6; k++) R(FLAG.x - k, FLAG.y + 6 - k, 1, 1, '#8a8a96');                 // the pole, out from the wall
  for (let i = 0; i < 9; i++) {
    const sw = Math.round(Math.sin(TAU * (36 * t / LOOP) + i * 0.8) * 1.1 * i / 8);
    R(FLAG.x - 5 - i, FLAG.y + 1 + sw, 1, 6, i < 3 ? '#c8323a' : i < 6 ? '#ece8e0' : '#2e8a4e');
  }
}
const SHOP_GLOW = [[15, 152, 0], [55, 150, 0], [90, 148, 1], [390, 148, 0], [422, 150, 1]];   // [x, y, cool]

/* ================= A picked weather (site/js/engine/weather.js) ================= */
// The Trevi has no falling thing of its own: the fountain's spray is its weather. Rain, snow and leaves fall behind the
// palace into the strip of sky, onto the basin and the terrace, and past the bottom of the frame, and catch the lights.
const WEATHER = weatherPlans([]);
const WX_LAND = [[30, 40], [156, 208], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: LANTS[0].x, y: LANTS[0].y + 18, rx: 30, ry: 42 }, { x: LANTS[1].x, y: LANTS[1].y + 18, rx: 30, ry: 42 },
  { x: 30, y: 160, rx: 44, ry: 26, col: '#ffd890' }, { x: 404, y: 156, rx: 34, ry: 22, col: '#ffd890' },
  { x: CX, y: 176, rx: 150, ry: 34, col: '#b8fff0' }, { x: CX, y: 110, rx: 110, ry: 60, col: '#ffe2b0' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS, density: 1.5 };
const WX_BB = { land: WX_LAND, lights: [{ x: BB.x + 22, y: BB.y + 8, rx: 30, ry: 20, col: '#ffe0d0' }, ...WX_LIGHTS], density: 1.5 };

/* ================= Pre-render everything static ================= */
let HL, SKY_C, SCENE_C, FRONT_C, CHURCH_C, SCAF_C, AD_C, PITCH, VIG_C;
function renderVignette() {
  const p = pix(W, H), c = [8, 4, 16];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  HL = {
    moon: makeHalo(12, '#fff0d0', 0.22, 1.4), glow: makeHalo(150, '#ffb060', 0.1, 1.3), niche: makeHalo(40, '#ffd08a', 0.16, 1.5),
    aqua: makeHalo(120, '#3ce0d0', 0.13, 1.4), uw: makeHalo(11, '#b8fff0', 0.3, 1.4), lamp: makeHalo(46, '#ffd49a', 0.18, 1.7),
    lampCore: makeHalo(10, '#fff4dc', 0.45, 1.4), bbLamp: makeHalo(6, '#ffe8c8', 0.5), bbGlow: makeHalo(30, '#ffd0b0', 0.1),
    shop: makeHalo(30, '#ffb866', 0.16, 1.5), shopCool: makeHalo(26, '#cfe0ff', 0.12, 1.5), flash: makeHalo(18, '#eaf2ff', 0.75, 1.3),
    glint: makeHalo(4, '#fff6c0', 0.7, 1), toy: makeHalo(5, '#7ad0ff', 0.55, 1.2), win: makeHalo(8, '#ffb45a', 0.14),
  };
  SKY_C = renderSky();
  const p = pix(W, H);
  renderSides(p);
  const pal = renderPalace();
  // the palace and the rocks go into the same buffer: copy the palace's pixels, then the rocks over its foot
  const pd = pal.c.getContext('2d').getImageData(0, 0, pal.c.width, pal.c.height).data;
  for (let y = 0; y < pal.c.height; y++) for (let x = 0; x < pal.c.width; x++) { const o = (y * pal.c.width + x) * 4; if (pd[o + 3]) p.set(pal.x + x, pal.y + y, [pd[o], pd[o + 1], pd[o + 2]]); }
  renderRocks(p); renderGround(p); renderChurch(p); renderSigns(p); renderLanterns(p);
  const ch = pix(W, H); renderChurch(ch); CHURCH_C = trim(ch);                   // and again on its own, laid over whoever is behind it
  // FRONT_C: everything from the waterline down that is not water, laid back over the basin's reflections each frame
  const f = pix(W, H);
  for (let y = WL - 2; y < H; y++) for (let x = 0; x < W; x++) if (!inBasin(x, y) && p.alpha(x, y) && !(y <= WL && x > 121 && x < 358)) { const o = (y * W + x) * 4; f.set(x, y, [p.d[o], p.d[o + 1], p.d[o + 2]]); }
  SCENE_C = p.done(); FRONT_C = trim(f);
  SCAF_C = renderScaffold(); AD_C = renderAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#f25fd0' });
  for (const east of [false, true]) for (let fl = 0; fl < 3; fl++) for (let col = 0; col < 5; col++) {
    const id = (east ? 100 : 0) + fl * 10 + col;
    if (winState(id) !== 'toggle') continue;
    const spr = renderSideLit(east, id);
    if (spr.c.width > 1) SIDE_WINS.push({ id, spr, n: 2 + Math.floor(hash(id, 1, 66) * 5), off: hash(id, 2, 66) });
  }
  PUFFS = [null]; for (let r = 1; r <= 6; r++) PUFFS.push(makePuff(r, '#e6fff8'));
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
/** render(t) paints the city's own coffee ad; { pitch } paints the site's pitch on its face; { billboard: false } leaves out
 *  the scaffold with its hoarding and lamps (the loop videos); { weather } draws a WEATHERS pick */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawGulls(t);
  halo(HL.glow, CX, 70, 1);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the palace, the side walls, the rocks
  ctx.drawImage(SCENE_C, 0, 0);
  halo(HL.niche, CX, 108, 0.9 + 0.1 * wave(t, 8, 0.2));
  drawPalaceWindows(t);
  for (const w of SIDE_WINS) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 5) < 0.5) blit(w.spr);
  drawRoosts(t);
  if (billboard) drawBillboard(t, pitch);
  // the water
  drawSpray(t, 0);
  drawFalls(t);
  drawSpray(t, 1);
  drawBasin(t);
  drawCoins(t, false);
  blit(FRONT_C);
  halo(HL.aqua, CX, 178, 0.9 + 0.1 * wave(t, 12, 0.1));
  for (const [x, y, cool] of SHOP_GLOW) halo(cool ? HL.shopCool : HL.shop, x, y, 0.9);
  drawLanterns(t);
  drawFlag(t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the piazza
  drawCrowd(t);
  drawCoins(t, true);
  drawToys(t);
  drawBubbles(t);
  drawSpray(t, 2);
  drawFlashes(t, reducedMotion);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'rome',
  name: 'Rome',
  tagline: 'Coins in the Trevi at 11 pm',
  timeZone: 'Europe/Rome',
  accent: '#f25fd0',
  focusX: CX,
  landmark: [192, 288],                                // the triumphal arch, its four columns: with the hoarding a phone zooms out to a band, the whole arch and the crowd in view
  landmarkTop: TIP,                                    // the row of the papal arms' tip, which landscape crops keep
  billboard: BB,
  ambience: { water: 0.75, crowd: 0.6, traffic: 0.06 },
  events: [
    ...COINS.filter(c => !c.wave).map(c => ({ t: +(c.t + COIN_AIR).toFixed(2), type: 'splash', x: Math.round(c.x + c.dx), gain: 0.2 })),
    { t: TOUR.toss + COIN_AIR + 0.1, type: 'splash', x: 232, gain: 0.8 }, { t: TOUR.toss + COIN_AIR + 0.5, type: 'splash', x: 262, gain: 0.6 },
    { t: BELL_T, type: 'bell', x: 455, gain: 0.6 }, { t: CHIME_T, type: 'chime', x: 455, gain: 0.4 },
    { t: 44, type: 'gull', x: 180 }, { t: 170.5, type: 'gull', x: 300 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
